import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  ArrowUpRight,
  Activity,
  CalendarCheck2,
  ChevronRight,
  CircleDot,
  Clock3,
  Cloud,
  CloudFog,
  CloudRain,
  CloudSnow,
  CloudSun,
  Compass,
  FileText,
  GitPullRequest,
  Layers,
  Sun,
  Target,
  TrendingUp,
  Users,
  UserPlus,
  X,
  Zap,
} from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import { TeamTodaySurface, shouldMarkViewed } from './TeamTodaySurface';
import type {
  TeamTodaySurfaceData,
  BriefAroundTailItem,
  BriefCitation,
  BriefSourceTopic,
  BriefTask,
  BriefTaskStream,
  BriefingSections,
  Diffstat,
  KnowledgeCapturedItem,
  MeetingBriefItem,
  NeedsInputItem,
  PriorActions,
  SharedDecision,
  StanceLean,
  StandupEntry,
  TeammateStance,
  WhatChangedItem,
  RelevantToYourWorkItem,
  WeatherTile as WeatherTileData,
} from '../../hooks/useTeamTodaySurface';
import { ConflictResolveModal } from '../brief/ConflictResolveModal';
import { MeetingPrepModal } from '../brief/MeetingPrepModal';
import { RdCardDialog, type RdCardConfig, type RdCardPrimary } from '../brief/RdCardDialog';
import { BriefPopoutBody, OverlapDetail } from '../brief/PopoutBody';
import { StoredPopoutBodyView } from '../brief/StoredPopoutBody';
import { RdBriefObjectDialog, type RdBriefObjectConfig } from '../brief/RdBriefObjectDialog';
import { resolveSourceTargets } from '../../lib/brief-source-target';
import { askNavTarget, askPrefillFor, briefObjectOpenControl, RULED_EXIT_LABEL } from '../../lib/brief-card-exits';
// The popout REACHABILITY MODEL's shared predicates (phase-4 GUARD). They live in the lib so the
// coverage guard asserts the same rules the render applies — a second copy here would let the guard
// pass against a model the UI no longer follows, which is precisely how 9 stored bodies went dark.
import { codaSuppressed, quietDayShell, TAIL_POPOUT_ROW_CAP } from '../../lib/brief-popout-coverage';
import { SourcePanels, GithubSourcePanel } from '../brief/SourcePanels';
import { TasksPanel } from '../brief/TasksPanel';
import { GithubStatsBoard } from '../brief/GithubStatsBoard';
import { DecisionLedger } from '../brief/DecisionLedger';
import { STREAM_LABELS, STREAM_ACTION, wmoCategory } from '../brief/primitives';
import { QuietDayState } from '../brief/QuietDayState';
import { RegenerateButton } from '../brief/RegenerateButton';
import { AskLauncher } from '../shared/AskLauncher';
import { briefDateKey, pickGreeting, MEETING_DENSE_MIN } from '../../lib/brief-greeting';
import { useAuditAction } from '../../hooks/useAuditAction';
import { useConflictOutcomes, outcomeClears } from '../../lib/conflict-local';
import { track } from '../../lib/analytics';

/*
 * BriefSurface — the version-branched web-brief renderer.
 *
 * THE INVARIANT (briefing-standardization spec §4 — "history as generated"): a briefing row is
 * immutable once stored, so pre-standardization rows must go on rendering exactly as before,
 * forever. The version branch below is deliberately the FIRST thing this file does.
 * `TeamTodaySurface` is FROZEN: this file imports only its two existing exports.
 *
 * `StandardBrief` (below) is the v2 renderer, re-skinned to the approved 2026-07 redesign
 * (cream tokens, Instrument Serif display, DM Mono eyebrows — Tailwind markup under the `.rd`
 * root). The DATA plumbing is unchanged from the pre-redesign renderer: every band's presence
 * is a plain array-non-empty check ("should this section show" was decided server-side at
 * generation time), the conflict verbs live in ConflictResolveModal, and nothing renders from
 * fixture data — bands without a backend rail show an honest placeholder or nothing.
 *
 * Mock-alignment pass (2026-07-26, Teddy's "no more text gen besides cards"): the LLM band
 * prose (bandNarratives/bandHooks) and the LLM masthead are no longer RENDERED here — section
 * headers are the mock's static/deterministic copy, and the masthead carries the mock's own
 * lines verbatim. LLM text survives only as CARD content (conflict/task/meeting cards, standup
 * sentences, around-card explanations). The Team-pulse band (ProgressCard/LeanMeter) is gone —
 * the mock's brief has no pulse; its payload fields still arrive for Slack + the v1 renderer.
 */

interface BriefSurfaceProps {
  data: TeamTodaySurfaceData;
  onNav: (id: string) => void;
  userName?: string;
  readOnly?: boolean;
  /** The reader's Supabase user id — the masthead greeting's per-reader key (§B5). Owned by the
   * route wrapper (App.tsx `useCurrentUserId`), never fetched here: the surface stays
   * presentational, and the id resolves alongside the brief instead of after it, so the greeting
   * is picked once and never swaps under the reader. Null only in the no-session/dev case. */
  readerId?: string | null;
}

type FlagWrongReason = 'wrong_person' | 'stale' | 'not_relevant_topic' | 'duplicate' | 'other';

const FLAG_WRONG_REASONS: Array<{ code: FlagWrongReason; label: string }> = [
  { code: 'wrong_person', label: 'Wrong person' },
  { code: 'stale', label: 'Stale' },
  { code: 'not_relevant_topic', label: 'Not relevant topic' },
  { code: 'duplicate', label: 'Duplicate' },
  { code: 'other', label: 'Other' },
];

const BriefReadContext = createContext<((cardKey: string) => void) | null>(null);

/** One observer target per surfaced, identity-bearing card. It observes the card rather than a
 * zero-height sentinel so the 50% visibility threshold has a concrete geometry. */
function useBriefReadSentinel(cardKey: string | null) {
  const markReached = useContext(BriefReadContext);
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!cardKey || !markReached || !ref.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        markReached(cardKey);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [cardKey, markReached]);

  return ref;
}

function BriefReadTracker({
  briefDay,
  enabled,
  itemsSurfaced,
  children,
}: {
  briefDay: string;
  enabled: boolean;
  itemsSurfaced: number;
  children: ReactNode;
}) {
  const reachedRef = useRef(new Set<string>());
  const sentDaysRef = useRef(new Set<string>());
  const idleTimerRef = useRef<number | null>(null);
  const cleanupTimerRef = useRef<number | null>(null);
  const currentDayRef = useRef(briefDay);
  const activeDayRef = useRef<string | null>(null);
  const activeItemsSurfacedRef = useRef(itemsSurfaced);
  const enabledRef = useRef(enabled);
  const surfacedRef = useRef(itemsSurfaced);

  currentDayRef.current = briefDay;
  enabledRef.current = enabled;
  surfacedRef.current = itemsSurfaced;

  const emit = useCallback((day: string, metrics?: { itemsReached: number; itemsSurfaced: number }) => {
    if (!enabledRef.current || !day || sentDaysRef.current.has(day)) return;
    sentDaysRef.current.add(day);
    track('brief_read_through', {
      items_reached: metrics?.itemsReached ?? reachedRef.current.size,
      items_surfaced: metrics?.itemsSurfaced ?? surfacedRef.current,
    });
  }, []);

  const scheduleIdle = useCallback(() => {
    if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => emit(currentDayRef.current), 60_000);
  }, [emit]);

  const markReached = useCallback((cardKey: string) => {
    if (!enabledRef.current || !currentDayRef.current || reachedRef.current.has(cardKey)) return;
    reachedRef.current.add(cardKey);
    scheduleIdle();
  }, [scheduleIdle]);

  useEffect(() => {
    // React StrictMode rehearses effect cleanup; defer the unmount emission one task so its second
    // setup cancels the rehearsal without suppressing the real unmount summary.
    if (cleanupTimerRef.current != null) {
      window.clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }
    const previousDay = activeDayRef.current;
    if (previousDay && previousDay !== briefDay && reachedRef.current.size > 0) {
      emit(previousDay, {
        itemsReached: reachedRef.current.size,
        itemsSurfaced: activeItemsSurfacedRef.current,
      });
    }
    reachedRef.current.clear();
    activeDayRef.current = briefDay;
    activeItemsSurfacedRef.current = itemsSurfaced;
    scheduleIdle();
    return () => {
      if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
      cleanupTimerRef.current = window.setTimeout(() => emit(briefDay), 0);
    };
  }, [briefDay, emit, scheduleIdle]);

  return <BriefReadContext.Provider value={markReached}>{children}</BriefReadContext.Provider>;
}

/** PostHog-only feedback control. It never calls an audit/mutation path and never hides a card. */
function FlagWrongControl({ itemId, onFlag }: { itemId: string; onFlag: (itemId: string, reason: FlagWrongReason) => void }) {
  const [open, setOpen] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  const flag = (reason: FlagWrongReason) => {
    onFlag(itemId, reason);
    setOpen(false);
    setFlagged(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setFlagged(false), 2_000);
  };

  return (
    <span className="relative inline-flex shrink-0">
      {flagged ? (
        <span className="px-1 text-xs text-acc" aria-label="Flagged">✓</span>
      ) : (
        <button
          type="button"
          aria-label="Flag this item as wrong"
          aria-expanded={open}
          className="rounded px-1 font-['DM_Mono'] text-sm leading-none text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-foreground"
          onClick={() => setOpen(value => !value)}
        >
          ⋯
        </button>
      )}
      {open && (
        <span role="menu" className="absolute right-0 top-6 z-20 w-40 rounded-lg border border-border bg-popover p-1 shadow-lg">
          {FLAG_WRONG_REASONS.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              role="menuitem"
              className="block w-full rounded px-2 py-1.5 text-left text-xs text-muted-foreground transition hover:bg-muted hover:text-foreground"
              onClick={() => flag(code)}
            >
              {label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export function BriefSurface(props: BriefSurfaceProps) {
  const openedDaysRef = useRef(new Set<string>());
  const briefDay = briefDateKey(props.data.generated_at);

  useEffect(() => {
    if (props.data.status !== 'ready' || !props.data.data || !briefDay || openedDaysRef.current.has(briefDay)) return;
    openedDaysRef.current.add(briefDay);
    track('brief_opened');
  }, [briefDay, props.data.data, props.data.status]);

  const version = props.data.data?.version ?? 1;
  // Pre-standardization rows are immutable and never regenerate (history spec:
  // "as generated"). They render through the frozen legacy renderer, always.
  if (version < 2) return <TeamTodaySurface {...props} />;
  return <StandardBrief {...props} />;
}

// ---- local render helpers ----

// A brief item's age. RELATIVE while the brief is live, ABSOLUTE once it is archived.
// (See brief-card-dates: 71% of live-item timestamps are <24h — relative reads as news; an
// archived brief re-rendering "2h ago" against today's clock would silently rot, so `frozen`
// pins those to the note's own date. The >=7d fallback carries the year outside the current one.)
function relativeTime(iso: string, frozen = false): string {
  if (frozen) return formatSourceDate(iso) ?? '';
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return formatSourceDate(iso) ?? '';
}

function formatCrumb(iso: string | null): string {
  if (!iso) return 'DELIVER';
  const d = new Date(iso);
  const day = d
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    .replace(',', '')
    .toUpperCase();
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `DELIVER · ${day} · ${time}`;
}

// Masthead date eyebrow — "Tuesday · October 17".
function formatDateEyebrow(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  return `${weekday} · ${date}`;
}

// "10a" / "2:30p" from minutes-since-local-midnight — shared by meeting rows and the now-line.
function minuteClock(min: number): string {
  const h24 = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h24 >= 12 ? 'p' : 'a';
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h}${ampm}` : `${h}:${String(m).padStart(2, '0')}${ampm}`;
}

// Calendar-row clock label — "10a" / "2:30p" from a meeting's startAt.
function formatClock(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return minuteClock(d.getHours() * 60 + d.getMinutes());
}

// A striped "free" row renders only for a REAL gap (≥45 min) between consecutive holds.
function freeGapAfter(prev: MeetingBriefItem, next: MeetingBriefItem): boolean {
  if (!prev.endAt || !next.startAt) return false;
  const a = new Date(prev.endAt).getTime();
  const b = new Date(next.startAt).getTime();
  if (isNaN(a) || isNaN(b)) return false;
  return b - a >= 45 * 60 * 1000;
}

// The day rail's working-day frame (Teddy 2026-07-28: the calendar stretches the WHOLE day — the
// mock's rail always opens and closes on a free row, so the card reads as the day's shape, not a
// list of holds). Local-clock arithmetic on the meetings' own timestamps; labels stay just "free"
// (bug-539 ruling — no invented uses for the time).
/*
 * How many citation rows a card's popout renders before the "+N more" (phase-B §B2/§B3).
 *
 * MIRRORS `CITATION_ROWS_DISPLAY_CAP` in the MVP's `lib/briefing/one-screen.ts`, which is where the
 * server charges its one-screen px budget for the citation block. The budget and what actually
 * renders have to agree, so the two constants are HAND-SYNCED: change both or neither.
 */
const CITATION_ROWS_DISPLAY_CAP = 2;

const DAY_START_HOUR = 9; // 9a
const DAY_END_HOUR = 18; // 6p
const FREE_ROW_MIN_GAP = 45; // minutes — same threshold as freeGapAfter

const hourClock = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h >= 12 ? 'p' : 'a'}`;

function localMinutes(iso?: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

/** A hatched free row on the day rail. `flex-1` so free stretches absorb the card's spare height —
 * the rail always reaches the bottom (Teddy 2026-07-28), and it's the FREE time that grows. */
function RdFreeRow({ clock }: { clock: string }) {
  return (
    <div className="grid min-h-10 flex-1 grid-cols-[31px_minmax(0,1fr)] gap-3 border-b border-border py-2.5 last:border-0">
      <span className="pt-1 font-['DM_Mono'] text-[10px] text-muted-foreground">{clock}</span>
      <div className="flex items-center rounded-lg bg-[repeating-linear-gradient(45deg,rgba(117,105,86,.07)_0,rgba(117,105,86,.07)_4px,transparent_4px,transparent_9px)] dark:bg-[repeating-linear-gradient(45deg,rgba(255,255,255,.06)_0,rgba(255,255,255,.06)_4px,transparent_4px,transparent_9px)] px-3 py-1.5 text-sm text-muted-foreground">
        free
      </div>
    </div>
  );
}

/** The rail's live now-line — current-time marker, re-slotted every minute (live brief only;
 * slot-level placement between rows, not a minute-proportional grid). */
function RdNowLine({ clock }: { clock: string }) {
  return (
    <div className="grid grid-cols-[31px_minmax(0,1fr)] items-center gap-3 py-1.5" data-testid="now-line">
      <span className="font-['DM_Mono'] text-[10px] font-medium text-acc">{clock}</span>
      <span className="relative block h-px bg-acc">
        <span className="absolute -left-0.5 top-1/2 size-[5px] -translate-y-1/2 rounded-full bg-acc" />
      </span>
    </div>
  );
}

function catLabel(cat: WhatChangedItem['category']): string {
  switch (cat) {
    case 'shipped':
      return 'Shipped';
    case 'still_building':
      return 'Still building';
    case 'discussed':
      return 'Discussed';
  }
}

function confidenceLabel(band: RelevantToYourWorkItem['confidenceBand']): string {
  switch (band) {
    case 'high':
      return 'High relevance';
    case 'medium':
      return 'Medium relevance';
    case 'low':
      return 'Low relevance';
    case 'uncertain':
      return 'Uncertain';
  }
}

// Local-dismiss key: conversationId when present, falling back to briefingItemId for
// conversationId-less streams (conflict tasks anchor on a decision edge, not a conversation).
function dismissKey(conversationId?: string | null, briefingItemId?: string | null): string | null {
  return conversationId ?? briefingItemId ?? null;
}

// Stream → card icon (redesign register; lucide).
const STREAM_ICON: Record<BriefTaskStream, typeof Target> = {
  conflict: ArrowLeftRight,
  overlap: ArrowLeftRight,
  meeting_prep: CalendarCheck2,
  linear: CircleDot,
  notion: FileText,
  github_review: GitPullRequest,
  invite: UserPlus,
  jira: CircleDot,
  focus: Target,
};

/** Quiet day — same shared state as the legacy surface's empty branch. */
function QuietDayShell({ onNav, readOnly }: { onNav: (id: string) => void; readOnly?: boolean }) {
  return (
    <QuietDayState onNav={onNav} readOnly={readOnly}>
      <button
        className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground"
        onClick={() => onNav('sources')}
      >
        Sources →
      </button>
    </QuietDayState>
  );
}

// webapp-actions A5: friendly labels for the scorer's suppression reasons.
const SUPPRESS_REASON_LABEL: Record<string, string> = {
  below_relevance: 'not relevant to your work',
  low_value: 'lower value today',
  abstained: 'low confidence',
  near_duplicate: 'near-duplicate of something shown',
  over_budget: "over today's limit",
};

// ---- redesign masthead tiles ----

const WX_ICON = { clear: Sun, partly: CloudSun, cloudy: Cloud, fog: CloudFog, rain: CloudRain, snow: CloudSnow, storm: Zap } as const;
const WX_LABEL = { clear: 'Clear skies', partly: 'Partly cloudy', cloudy: 'Overcast', fog: 'Foggy', rain: 'Rain around', snow: 'Snow around', storm: 'Storms possible' } as const;

/** Masthead weather tile (real WeatherTile payload; °F display like the pre-redesign tile). */
function RdWeatherTile({ weather }: { weather: WeatherTileData }) {
  const toF = (c: number) => Math.round((c * 9) / 5 + 32);
  const temp = weather.tempC != null ? `${toF(weather.tempC)}°` : null;
  if (!temp) return null;
  const cat = wmoCategory(weather.weatherCode);
  const Icon = cat ? WX_ICON[cat] : Sun;
  const hi = weather.tempMaxC != null ? toF(weather.tempMaxC) : null;
  const lo = weather.tempMinC != null ? toF(weather.tempMinC) : null;
  const sub = [cat ? WX_LABEL[cat] : null, hi != null && lo != null ? `H${hi} L${lo}` : null].filter(Boolean).join(' · ');
  return (
    <div className="rounded-[20px] border border-border bg-[#f1e8d8] p-4 shadow-[0_12px_25px_rgba(88,69,42,.06)] dark:bg-muted">
      <div className="flex items-start justify-between">
        <span className="grid size-9 place-items-center rounded-xl bg-[#fbf6ec] text-[#bd774f] shadow-sm dark:bg-card">
          <Icon size={19} />
        </span>
        {weather.locationLabel && (
          <span className="font-['DM_Mono'] text-[10px] uppercase text-muted-foreground">{weather.locationLabel}</span>
        )}
      </div>
      <p className="mt-5 font-['Instrument_Serif'] text-4xl leading-8">{temp}</p>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** Health tile — honest placeholder: no health rail exists yet (CI / queue watchers land here). */
function RdHealthTile() {
  return (
    <div className="rounded-[20px] border border-dashed border-[#ddd2c0] bg-card/60 p-4 dark:border-border">
      <div className="flex items-start justify-between">
        <span className="grid size-9 place-items-center rounded-xl bg-muted text-muted-foreground">
          <Activity size={18} />
        </span>
        <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">HEALTH</span>
      </div>
      <p className="mt-5 font-['Instrument_Serif'] text-[26px] leading-7 text-muted-foreground">Nothing wired</p>
      <p className="mt-1 text-sm text-muted-foreground">No health signals yet — CI and queue watchers will land here.</p>
    </div>
  );
}

// ---- redesign band cards ----

/** The card eyebrow's trailing marks.
 *  - `spotlight` (B3 item 3): the ⚠️ Spotlight axis the Slack DM has rendered since v2 and the SPA
 *    never read. At most one card carries it — it means "look here first", not a badge.
 *  - `onActions` (B3 item 5): the reciprocal half of a two-way law ActionsSurface already keeps its
 *    side of ("on today's brief" / "not on today's brief", indexMarks). LIVE BRIEF ONLY — Actions
 *    lists TODAY, so on an archived brief the claim is simply false; call sites gate it on
 *    `!readOnly`. */
function RdCardMarks({ spotlight, onActions }: { spotlight?: boolean; onActions?: boolean }) {
  return (
    <>
      {spotlight && <span className="brief-spotlight-mark text-acc"> · Spotlight</span>}
      {onActions && <span className="brief-actions-mark text-muted-foreground"> · also on Actions</span>}
    </>
  );
}

/** Conflict card (rose register) — Resolve opens the honest-verb popup; X is a LOCAL hide only
 * (bug-423: never a relevance audit; the decision edge stays open and returns next brief). */
function RdConflictCard({
  task,
  onResolve,
  onDismiss,
  onFlagWrong,
  readOnly,
  spotlight,
}: {
  task: BriefTask;
  onResolve: (t: BriefTask) => void;
  onDismiss: (conversationId: string | null, briefingItemId?: string | null) => void;
  onFlagWrong: (itemId: string, reason: FlagWrongReason) => void;
  readOnly: boolean;
  spotlight?: boolean;
}) {
  const evalBand = task.band === 'low' || task.band === 'uncertain';
  const itemId = task.briefingItemId ?? task.conversationId ?? null;
  const readRef = useBriefReadSentinel(itemId ? `task:${task.taskId}:${itemId}` : null);
  return (
    <article ref={readRef} className="brief-conflict-card group rounded-[19px] border border-[#e2aa9c] bg-[#fdf0e7] p-4 shadow-[0_8px_20px_rgba(185,92,75,.06)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_25px_rgba(185,92,75,.12)] dark:border-destructive/40 dark:bg-destructive/10">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[#f5d8ce] text-[#b95c4b] shadow-sm dark:bg-destructive/20 dark:text-destructive">
          <ArrowLeftRight size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-[15px] font-semibold leading-5">{task.title}</h3>
            {!readOnly && (
              <button
                onClick={() => onDismiss(task.conversationId ?? null, task.briefingItemId ?? null)}
                aria-label={`Dismiss ${task.title} for today`}
                className="text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-foreground"
              >
                <X size={15} />
              </button>
            )}
            {itemId && <FlagWrongControl itemId={itemId} onFlag={onFlagWrong} />}
          </div>
          {task.detail && <p className="mt-1 text-sm leading-5 text-muted-foreground">{task.detail}</p>}
          <p className="mt-1.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.14em] text-[#bd5b4c] dark:text-destructive">
            {evalBand ? 'Possible conflict · confirm it' : 'Conflict · needs a call'}
            {task.dueLabel ? ` · ${task.dueLabel}` : ''}
            <RdCardMarks spotlight={spotlight} onActions={!readOnly && isOnActions(task)} />
          </p>
        </div>
      </div>
      {!readOnly && task.briefingItemId && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => onResolve(task)}
            className="rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3 py-1 text-sm transition hover:border-foreground hover:bg-foreground hover:text-card dark:border-border dark:bg-card"
          >
            Resolve
          </button>
        </div>
      )}
    </article>
  );
}

/** Non-conflict task card (cream register): stream icon + title + detail, verb from the stream. */
function RdTaskCard({
  task,
  onOpen,
  onDismiss,
  onItemExpanded,
  onFlagWrong,
  readOnly,
  spotlight,
}: {
  task: BriefTask;
  // C1 (walk 2026-08-02) — the priority column flattens to ONE drill-in depth: the verb always opens
  // the same composed panel, which carries the same exits as every other card in the brief. The prop
  // is REQUIRED, and that is the point. It replaces an open-handler ladder
  // (onExpand ?? conversationId ?? externalUrl ?? null) whose last rung was `null` while the entire
  // verb block was gated on it — so a focus task with neither id rendered with nothing to click
  // anywhere on the card (bug-614). A verb-less task card is now unrepresentable, not merely
  // filtered: [Ask] needs no ids, so the panel this opens always has a working exit.
  onOpen: () => void;
  onDismiss: (conversationId: string | null, briefingItemId?: string | null) => void;
  onItemExpanded: (itemId: string, type: string) => void;
  onFlagWrong: (itemId: string, reason: FlagWrongReason) => void;
  readOnly: boolean;
  spotlight?: boolean;
}) {
  const Icon = STREAM_ICON[task.stream] ?? Clock3;
  const action = STREAM_ACTION[task.stream] ?? 'Open';
  const itemId = task.briefingItemId ?? task.conversationId ?? null;
  const readRef = useBriefReadSentinel(itemId ? `task:${task.taskId}:${itemId}` : null);
  return (
    <article ref={readRef} className="group rounded-[19px] border border-border bg-card p-4 shadow-[0_8px_20px_rgba(88,69,42,.035)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_25px_rgba(88,69,42,.09)]">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[#eee5d5] text-[#887157] shadow-sm dark:bg-muted dark:text-muted-foreground">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-[15px] font-semibold leading-5">{task.title}</h3>
            {!readOnly && dismissKey(task.conversationId, task.briefingItemId) && (
              <button
                onClick={() => onDismiss(task.conversationId ?? null, task.briefingItemId ?? null)}
                aria-label={`Dismiss ${task.title}`}
                className="text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-foreground"
              >
                <X size={15} />
              </button>
            )}
            {itemId && <FlagWrongControl itemId={itemId} onFlag={onFlagWrong} />}
          </div>
          {task.detail && <p className="mt-1 text-sm leading-5 text-muted-foreground">{task.detail}</p>}
          <p className="mt-1.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.14em] text-muted-foreground">
            {STREAM_LABELS[task.stream] ?? task.stream}
            {task.dueLabel ? ` · ${task.dueLabel}` : ''}
            <RdCardMarks spotlight={spotlight} onActions={!readOnly && isOnActions(task)} />
          </p>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={() => {
            if (itemId) onItemExpanded(itemId, task.stream);
            onOpen();
          }}
          className="rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3 py-1 text-sm transition hover:border-foreground hover:bg-foreground hover:text-card dark:border-border dark:bg-card"
        >
          {action}
        </button>
      </div>
    </article>
  );
}

/** Calendar row — a meeting inside the Your-day panel. `active` (needs you) gets the rose accent. */
function RdCalendarRow({
  meeting,
  onPrep,
  frozen,
}: {
  meeting: MeetingBriefItem;
  onPrep?: () => void;
  frozen: boolean;
}) {
  const time = formatClock(meeting.startAt) ?? meeting.when ?? '—';
  const active = meeting.needsAction || meeting.category === 'action';
  const joinUrl = meeting.meetingUrl || meeting.htmlLink || null;
  return (
    <div className="grid grid-cols-[31px_minmax(0,1fr)] gap-3 border-b border-border py-2.5 last:border-0">
      <span className="pt-1 font-['DM_Mono'] text-[10px] text-muted-foreground">{time}</span>
      <div className={`rounded-xl border p-3 ${active ? 'border-[#e2aa9c] bg-[#fdf0e7] dark:border-destructive/40 dark:bg-destructive/10' : 'border-[#ddd2c0] bg-[#f2ebdf] dark:border-border dark:bg-muted'}`}>
        <div className="flex items-center justify-between gap-3">
          <p className="font-medium">
            {meeting.title}
            {active && <span className="ml-2 text-xs font-normal text-[#bd5b4c] dark:text-destructive">needs you</span>}
          </p>
          <span className="flex shrink-0 items-center gap-1.5">
            {onPrep && !frozen && (
              <button
                onClick={onPrep}
                className="rounded-full border border-[#d6c9b7] bg-[#fffaf2] px-3 py-0.5 text-xs transition hover:border-foreground dark:border-border dark:bg-card"
              >
                Prep
              </button>
            )}
            {joinUrl && (
              <a
                href={joinUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-[#d6c9b7] bg-[#fffaf2] px-3 py-0.5 text-xs transition hover:border-foreground dark:border-border dark:bg-card"
              >
                Join
              </a>
            )}
          </span>
        </div>
        {(meeting.why || meeting.attendeesSummary) && (
          <p className="mt-1 text-sm leading-5 text-muted-foreground">{meeting.why || meeting.attendeesSummary}</p>
        )}
        {/* webapp-actions §6.5 #4: the event's own agenda (calendar description, capped server-side). */}
        {meeting.agenda && (
          <p className="mtg-agenda mt-1.5 border-t border-black/[.06] pt-1.5 text-[13px] leading-5 text-muted-foreground dark:border-white/10">
            {meeting.agenda}
          </p>
        )}
        {(meeting.relatedWork?.length ?? 0) > 0 && (
          <span className="mt-1.5 flex flex-wrap gap-1.5">
            {meeting.relatedWork!.slice(0, 3).map(w => (
              <span
                key={w}
                className="rounded-full bg-[#eee5d5] px-2 py-0.5 font-['DM_Mono'] text-[9px] text-[#847258] dark:bg-muted dark:text-muted-foreground"
              >
                {w}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

/** GitHub-enriched diffstat line ("branch → base · +X −Y · N files") in the dark band's register.
 * Same data as the pre-redesign DiffstatRow — only when the change item carries it. */
function RdDiffstat({ diffstat }: { diffstat: Diffstat }) {
  const adds = diffstat.additions ?? 0;
  const dels = diffstat.deletions ?? 0;
  const files = diffstat.filesChanged ?? 0;
  const branch = diffstat.branchMerged
    ? `${diffstat.branchMerged}${diffstat.baseBranch ? ` → ${diffstat.baseBranch}` : ''}`
    : null;
  return (
    <span className="diffstat mt-2 flex flex-wrap items-center gap-x-2 font-['DM_Mono'] text-[10px] text-white/45">
      {branch && <span className="truncate">{branch}</span>}
      <span className="text-[#a8d5ae]">+{adds}</span>
      <span className="text-[#e0a3a3]">−{dels}</span>
      <span>
        {files} file{files === 1 ? '' : 's'}
      </span>
    </span>
  );
}

/** Around-your-work card on the purple band — a change or a relevant item, click → conversation.
 *
 * B3 item 6: the card now carries the same × the conflict/task cards have had since the redesign.
 * It is the web's ONLY producer of the relevance learning loop's reject signal — a relevant item
 * carries a briefingItemId, so its dismiss rides the existing `reject_relevance` audit that
 * team/today's filterActionedItems already honours; a CHANGE item has no audit row, so its × is a
 * local hide. The dismiss button is a SIBLING of the open button (never nested inside it — a button
 * inside a button is invalid markup and swallows the inner click). */
function RdAroundCard({
  index,
  eyebrow,
  title,
  body,
  meta,
  onOpen,
  onDismiss,
  itemId,
  itemType,
  onItemExpanded,
  onFlagWrong,
  diffstat,
  topMatch,
}: {
  index: number;
  eyebrow: string;
  title: string;
  body?: string | null;
  meta?: string | null;
  onOpen?: () => void;
  onDismiss?: () => void;
  itemId?: string | null;
  itemType?: string;
  onItemExpanded: (itemId: string, type: string) => void;
  onFlagWrong: (itemId: string, reason: FlagWrongReason) => void;
  diffstat?: Diffstat | null;
  topMatch?: boolean;
}) {
  const readRef = useBriefReadSentinel(itemId ? `around:${index}:${itemId}` : null);
  const inner = (
    <>
      <span
        className={`absolute right-3 top-3 font-['DM_Mono'] text-[10px] text-white/30 transition ${onDismiss ? 'group-hover:opacity-0' : ''}`}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <p className="flex items-center gap-2 pr-7 font-['DM_Mono'] text-[9px] uppercase tracking-[.14em] text-[#c7c2d4]">
        {eyebrow}
        {topMatch && (
          <span className="topmatch rounded-full border border-white/25 bg-white/10 px-2 py-0.5 normal-case tracking-normal text-[#ded7ec]">
            Top match
          </span>
        )}
      </p>
      <p className="mt-3 pr-5 text-[15px] font-medium leading-5 text-[#fbf5e9]">{title}</p>
      {body && <p className="mt-1.5 text-sm leading-5 text-[#c2bcd2]">{body}</p>}
      {diffstat && <RdDiffstat diffstat={diffstat} />}
      <span className="mt-3 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {meta && <span className="truncate font-['DM_Mono'] text-[10px] text-white/40">{meta}</span>}
        </span>
        {onOpen && (
          <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-[#c7c2d4] transition-colors group-hover:text-[#ded7ec]">
            Open <ArrowUpRight size={13} />
          </span>
        )}
      </span>
    </>
  );
  return (
    <article
      ref={readRef}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[.05] shadow-[0_7px_14px_rgba(0,0,0,.08)] transition ${
        // The lift is an affordance for the open click — a card with nowhere to go must not claim one.
        onOpen ? 'hover:-translate-y-0.5 hover:bg-white/[.09] hover:shadow-md' : ''
      }`}
    >
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss ${title}`}
          className="brief-around-dismiss absolute right-2.5 top-2.5 z-10 text-white/50 opacity-0 transition group-hover:opacity-100 hover:text-white"
        >
          <X size={14} />
        </button>
      )}
      {itemId && <span className="absolute right-8 top-2.5 z-10"><FlagWrongControl itemId={itemId} onFlag={onFlagWrong} /></span>}
      {onOpen ? (
        <button onClick={() => {
          if (itemId && itemType) onItemExpanded(itemId, itemType);
          onOpen();
        }} className="relative block w-full p-4 text-left">
          {inner}
        </button>
      ) : (
        <div className="relative p-4 text-left">{inner}</div>
      )}
    </article>
  );
}

// ---- B3 item 1 (D7): DECISIONS FORMING ----

// figure b: a teammate's lean → 0-100% position on the for↔against track (poleFor left,
// poleAgainst right). no_signal has no position — callers filter it out (the backend already
// excludes it; this is defensive). Values copied from the FROZEN v1 renderer's private copy
// (TeamTodaySurface.tsx): that file renders history "as generated" and may not be refactored, so
// the constant cannot be lifted out of it into a shared home.
const LEAN_PERCENT: Record<Exclude<StanceLean, 'no_signal'>, number> = {
  strongly_for: 4,
  leaning_for: 28,
  neutral_undecided: 50,
  leaning_against: 72,
  strongly_against: 96,
};

/** One forming decision: the open question, where each teammate sits on it, and their positions.
 * Content follows the v1 precedent (lean meter + stance list); the register is the redesign's. */
function RdDecisionCard({ decision, frozen, onOpen }: { decision: SharedDecision; frozen: boolean; onOpen?: () => void }) {
  // `?? []`: the BFF returns the stored briefing JSON verbatim (no Zod re-parse), so the contract's
  // stances.default([]) is NOT applied at runtime — guard against a missing array.
  const placed = (decision.stances ?? []).filter(
    (s): s is TeammateStance & { lean: Exclude<StanceLean, 'no_signal'> } => s.lean !== 'no_signal',
  );
  const entities = [...new Set(decision.sharedEntities ?? [])];
  return (
    <article
      // Spec §12 (review fix): with a stored popout body the card opens the ruled dialog.
      // The mouse affordance is a plain onClick on the article (no role="button" — that would
      // flatten the card's heading/list semantics for assistive tech); keyboard + AT get the
      // real button in the header row below. Without a body: inert, no dead affordance.
      {...(onOpen ? { onClick: onOpen } : {})}
      className={`brief-decision-card rounded-[19px] border border-border bg-card p-4 shadow-[0_8px_20px_rgba(88,69,42,.035)]${onOpen ? ' cursor-pointer transition hover:-translate-y-0.5 hover:border-acc hover:shadow-md' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[.14em] text-muted-foreground">
          Decision forming
          {decision.teamProjectName ? ` · ${decision.teamProjectName}` : ''}
        </p>
        <span className="flex shrink-0 items-center gap-2">
          {decision.timestamp && (
            <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">
              {relativeTime(decision.timestamp, frozen)}
            </span>
          )}
          {onOpen && (
            <button
              type="button"
              data-testid="decision-card-open"
              aria-label={`Open: ${decision.decisionQuestion}`}
              onClick={e => {
                e.stopPropagation();
                onOpen();
              }}
              className="grid size-6 place-items-center rounded-full border border-border bg-muted text-muted-foreground transition hover:text-foreground"
            >
              <ChevronRight size={13} />
            </button>
          )}
        </span>
      </div>
      <h3 className="mt-2 text-[15px] font-semibold leading-5">{decision.decisionQuestion}</h3>
      {placed.length > 0 && (
        <div className="mt-3.5">
          <div className="relative h-6 rounded-full bg-muted">
            {placed.map((s, si) => (
              <span
                key={s.conversationId || `mk-${si}`}
                className="absolute top-1/2 grid size-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[#d9b28e] text-[10px] font-semibold text-[#4a372b]"
                style={{ left: `${LEAN_PERCENT[s.lean]}%` }}
                title={`${s.memberName} — ${s.positionSummary}`}
              >
                {s.initial || s.memberName.charAt(0)}
              </span>
            ))}
          </div>
          <div className="mt-1.5 flex justify-between gap-3 font-['DM_Mono'] text-[10px] text-muted-foreground">
            <span className="truncate">{decision.poleFor}</span>
            <span className="truncate text-right">{decision.poleAgainst}</span>
          </div>
        </div>
      )}
      {entities.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {entities.map(e => (
            <span
              key={e}
              className="rounded-full bg-[#eee5d5] px-2 py-0.5 font-['DM_Mono'] text-[9px] text-[#847258] dark:bg-muted dark:text-muted-foreground"
            >
              {e}
            </span>
          ))}
        </div>
      )}
      {placed.length > 0 && (
        <ul className="mt-3 space-y-1.5 border-t border-border pt-3 text-sm leading-5 text-muted-foreground">
          {placed.map((s, si) => (
            <li key={s.conversationId || `st-${si}`}>
              <b className="font-semibold text-foreground">{s.memberName}</b> — {s.positionSummary}
              {s.supportingQuote ? <span className="italic"> “{s.supportingQuote}”</span> : null}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// ---- B3 item 2 (D7 rails): the prior-brief receipt ----

/** "What happened to your last brief" — the five counts prior-actions.ts computes and generate.ts
 * has persisted on every brief since 2026-07-27, with no reader until now. Renders only the parts
 * that are actually non-zero, so the line never pads itself. */
function priorActionsLine(prior: PriorActions): string | null {
  const parts: string[] = [];
  if (prior.actedCount) parts.push(`${prior.actedCount} acted on`);
  if (prior.dismissedCount) parts.push(`${prior.dismissedCount} dismissed`);
  if (prior.pendingCount) parts.push(`${prior.pendingCount} still open`);
  if (prior.settledSinceCount) parts.push(`${prior.settledSinceCount} settled since`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ---- B3 item 4: restricted-item provenance ----

/** WHY a restricted row is in this reader's brief. The three strings are the ones already shipped
 * in the Slack DM (format-slack-message.ts provenanceSuffix, Ryan-approved 2026-07-20) — reused
 * verbatim minus Slack's markdown italics so a citation and its DM cannot explain the same note
 * differently. Empty for whole-team rows. */
function restrictedProvenance(fromRestricted: boolean | null | undefined, site: string | null | undefined): string | null {
  if (!fromRestricted) return null;
  if (site === 'granola') return 'from a meeting you attended';
  if (site === 'email') return 'from an email thread you were on';
  return 'shared with you';
}

/** A citation member note's explicit visibility class (BriefCitation.visibility). `public` is the
 * default and says nothing worth a label; the two restricted classes do. */
function visibilityLabel(visibility: string | null | undefined): string | null {
  if (visibility === 'audience_shared') return 'shared with you';
  if (visibility === 'own_private') return 'private to you';
  return null;
}

// ---- B3 item 5: the dual-presence mark ----

/** Is this brief task ALSO listed on the Actions surface? Mirrors ActionsSurface's own selection
 * (conflicts need a briefingItemId to have a room there; every non-conflict, non-focus stream is a
 * mechanical task row). Derived from the payload the brief already holds — no extra fetch. */
function isOnActions(task: BriefTask): boolean {
  return task.stream === 'conflict' ? !!task.briefingItemId : task.stream !== 'focus';
}

function StandardBrief({ data, onNav, readOnly = false, readerId = null }: BriefSurfaceProps) {
  const queryClient = useQueryClient();
  // D4 (decision walk 2026-08-12): keyed on the brief's identity, not a one-shot boolean. A
  // rebuild clears viewed_at server-side and restamps generated_at, and the D1 poll swaps the new
  // payload in under this mounted component — a boolean latch would leave the rebuilt brief
  // unstamped forever. Keying on generated_at re-stamps once per NEW brief while still never
  // double-POSTing for the same one (StrictMode double-invoke, invalidation re-renders).
  const viewedStampedFor = useRef<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const audit = useAuditAction();
  // The conflict currently open in the resolution modal, or null when closed.
  const [resolveTask, setResolveTask] = useState<BriefTask | null>(null);
  // The coda's "+N discoveries" expands in place to the actual captured items.
  const [codaOpen, setCodaOpen] = useState(false);
  // A5: the suppression receipt's "+N didn't make it" expands in place to the reason tally.
  const [suppressOpen, setSuppressOpen] = useState(false);
  // §6.5 (A3): the meeting whose prep document is open, or null when closed.
  const [prepMeeting, setPrepMeeting] = useState<MeetingBriefItem | null>(null);
  // Mock-alignment: the expandable-card popup (the mock's CardDialog) currently open, or null.
  const [openCard, setOpenCard] = useState<RdCardConfig | null>(null);
  // The RULED brief-object dialog (stored popout bodies, spec §5) — a separate state from
  // `openCard` because the drill-in surfaces keep handing RdCardConfig to setOpenCard; only the
  // three brief-object call sites below ever set this, and only when the brief carries a stored
  // body for the card. The scrim makes the two mutually exclusive in practice.
  const [openObjectCard, setOpenObjectCard] = useState<RdBriefObjectConfig | null>(null);
  // One item, two views: conflicts already resolved/snoozed this session (here or on the
  // Actions page) drop off the brief immediately, without waiting for the next regen.
  const conflictOutcomes = useConflictOutcomes();
  // The day rail's live clock (minutes since local midnight), re-read every minute so the
  // now-line tracks the day (Teddy 2026-07-28: "close to live"). Live brief only.
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  // The masthead greeting is picked ONCE per loaded brief (§B5 / review F10). The rail's clock
  // above ticks on purpose; the greeting's must not — it is read only for the time-of-day overlay,
  // and a headline that swaps mid-session reads as a second, contradicting brief. Keyed on the
  // brief's identity, so navigating to another briefing re-reads the clock and the same brief
  // never does. Declared with the other hooks: the early returns below make anything lower
  // conditional.
  const greetingClock = useMemo(() => new Date(), [data.generated_at]);

  // Mark-viewed: same predicate as the legacy surface; see viewedStampedFor above for why the
  // dedup is per-brief (generated_at) rather than per-mount (D4 re-stamp after a rebuild swap).
  useEffect(() => {
    const briefKey = data.generated_at ?? '';
    if (shouldMarkViewed(readOnly, data.status, data.viewed_at) && viewedStampedFor.current !== briefKey) {
      viewedStampedFor.current = briefKey;
      apiFetch('/api/surfaces/team/today/viewed', { method: 'POST' })
        .then(() => queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-today'] }))
        .catch(() => {});
    }
  }, [data.viewed_at, data.status, data.generated_at, queryClient, readOnly]);

  useEffect(() => {
    if (readOnly) return; // archived briefs get no ticking clock (frozen "as generated")
    const tick = setInterval(() => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    }, 60_000);
    return () => clearInterval(tick);
  }, [readOnly]);

  // Guarded by BriefSurface's version check. Defensive only.
  if (!data.data) return null;
  const sections = data.data;

  const tasksAll = sections.tasks ?? [];
  const tasksCollapsedCount = sections.tasksCollapsedCount ?? 0;
  // Per-source-only panels: cap-cut tasks without a source panel have no panel receipt anymore —
  // fold them into the same "below today's threshold" tail (they were cut as the weakest by value).
  const tasksOverflowCount = sections.tasksOverflowCount ?? 0;
  const meetings = sections.meetings ?? [];
  const around = sections.around ?? {};
  const changes = around.changes ?? [];
  const relevant = around.relevant ?? [];
  const changesCollapsedCount = around.changesCollapsedCount ?? 0;
  const relevantCollapsedCount = around.relevantCollapsedCount ?? 0;
  // D5/D14 (B2): the General section's expansion. `undefined` = a briefing generated before the
  // field existed → keep the old subtraction receipt below (an archived brief must not lose its
  // "+N more" line). A present list — even an empty one — is the authority: the reader's "N more"
  // is `generalTail.length`, so the number and the sheet it opens are the same fact.
  const generalTail = around.tail ?? null;
  const suppressed = around.suppressed ?? null;
  const suppressedCount = suppressed?.count ?? 0;
  // Static sections (Teddy 2026-07-27): panels always render — empty ones show skeletons — so the
  // raw array goes through; SourcePanels/GithubSourcePanel own the per-source empty states.
  const sourcePanels = sections.sourcePanels ?? [];

  // Phase-2 band order: the server caps changes+relevant at 3 COMBINED and stamps summarySlot
  // (slot 0 reserved for company news). Sort is stable, so legacy payloads (no slots) keep the
  // changes-then-relevant order.
  type BandCard = { kind: 'change'; change: WhatChangedItem } | { kind: 'relevant'; rel: RelevantToYourWorkItem };
  // B3 item 6: an around card's dismiss key is NAMESPACED so a card and a task that happen to share
  // a conversationId can never take each other down.
  const aroundKey = (conversationId?: string | null, briefingItemId?: string | null): string | null => {
    const key = dismissKey(conversationId, briefingItemId);
    return key ? `around:${key}` : null;
  };
  const bandCards: BandCard[] = [
    ...changes.map((change): BandCard => ({ kind: 'change', change })),
    ...relevant.map((rel): BandCard => ({ kind: 'relevant', rel })),
  ]
    .sort((a, b) => {
      const slot = (c: BandCard) => (c.kind === 'change' ? c.change.summarySlot : c.rel.summarySlot) ?? 99;
      return slot(a) - slot(b);
    })
    .filter(c => {
      const key =
        c.kind === 'change' ? aroundKey(c.change.conversationId) : aroundKey(c.rel.conversationId, c.rel.briefingItemId);
      return !key || !dismissed.has(key);
    });
  const knowledgeCaptured = sections.knowledgeCaptured ?? [];
  // B4 (D9): the coda is the reader's OWN notes by construction, so a non-solo reader is being shown
  // their own work back. Suppressed at RENDER time only — `knowledgeCaptured` above stays raw
  // because isQuietDay below is a claim about the DAY, not about what this render chose to show.
  const codaItems = codaSuppressed(sections) ? [] : knowledgeCaptured;
  const standup = sections.standup ?? [];
  const priorActions = sections.priorActions ?? null;
  const priorLine = priorActions ? priorActionsLine(priorActions) : null;
  const sharedDecisions = sections.sharedDecisions ?? [];
  // Task → overlap-item join (walk D15). Client-side and free: an overlap task and its
  // needsYourInput item are keyed on the SAME conversation (memberAConvId), so the item's shipped
  // detail reaches the popout with no payload change.
  const needsByConversation = new Map<string, NeedsInputItem>();
  for (const n of sections.needsYourInput ?? []) {
    if (n.conversationId && !needsByConversation.has(n.conversationId)) needsByConversation.set(n.conversationId, n);
  }
  /* Task → meeting join (phase-4 GAP 2a), the same free client-side shape as the overlap join
   * above. A meeting_prep task's id IS `meeting_prep:<eventId>` (MVP task-streams.ts), and
   * sections.meetings already carries that event, so the card can reach the prep document with no
   * payload change and no new key. Built from the taskId rather than re-deriving the prefix at the
   * call site so the ONE string that couples the two repos sits in one place. */
  const meetingByTaskId = new Map<string, MeetingBriefItem>(
    meetings.filter(m => !!m.eventId).map(m => [`meeting_prep:${m.eventId}`, m]),
  );

  // Shared with the reachability model (lib/brief-popout-coverage): a quiet day renders no cards,
  // so no stored body has an opener — the guard has to know that too.
  if (quietDayShell(sections)) return <QuietDayShell onNav={onNav} readOnly={readOnly} />;

  // bug-423 / D-ACT15: dismissing a conflict is a LOCAL hide only — no audit, no eval label. A
  // conflict's honest "not now" is the resolve modal's Snooze verb (it writes the per-user
  // decision_conflict_snooze row); routing the × through reject_relevance would teach the relevance
  // calibrator that the conflict's own source conversations are irrelevant.
  const dismissConflict = (conversationId: string | null, briefingItemId?: string | null) => {
    const key = dismissKey(conversationId, briefingItemId);
    if (key) setDismissed(prev => new Set(prev).add(key));
  };

  // Non-conflict cards keep the DURABLE dismiss the legacy surface has had since bug-315: hide
  // optimistically, then persist a reject_relevance audit so the item stays gone on reload. The
  // server half is already live for this exact stream — team/today's filterActionedItems drops any
  // task whose briefingItemId has a non-draft briefing_items row. The redesign wired every × to
  // dismissConflict, so the conflict-only local rule above silently took task cards down with it.
  // No briefingItemId (legacy briefings) → local-only hide, same as TeamTodaySurface.
  const dismissTask = (conversationId: string | null, briefingItemId?: string | null) => {
    const key = dismissKey(conversationId, briefingItemId);
    if (key) setDismissed(prev => new Set(prev).add(key));
    if (briefingItemId) audit.mutate({ itemId: briefingItemId, action: 'reject_relevance' });
  };

  // B3 item 6: the around band's × — the web's ONLY producer of the relevance learning loop's
  // reject signal. A RELEVANT item carries a briefingItemId, so its dismiss is durable through the
  // same reject_relevance audit the task cards write (team/today's filterActionedItems already
  // applies it to `around.relevant`). A CHANGE item has no audit row → local hide only, exactly like
  // a legacy task card. Deliberately NOT routed through dismissConflict: bug-423/D-ACT15 made the
  // CONFLICT × session-local; the around band is the case that rule carved out, not the one it bans.
  const dismissAround = (conversationId?: string | null, briefingItemId?: string | null) => {
    const key = aroundKey(conversationId, briefingItemId);
    if (key) setDismissed(prev => new Set(prev).add(key));
    if (briefingItemId) audit.mutate({ itemId: briefingItemId, action: 'reject_relevance' });
  };

  // §6.6: open the honest-verb resolution modal (band-gated inside the modal).
  const openResolve = (t: BriefTask) => {
    const itemId = t.briefingItemId ?? t.conversationId;
    if (t.briefingItemId) {
      if (itemId) track('item_expanded', { item_id: itemId, type: 'conflict' });
      setResolveTask(t);
    }
  };
  const handleResolved = (t: BriefTask) => {
    dismissConflict(t.conversationId ?? null, t.briefingItemId ?? null);
    setResolveTask(null);
  };

  const visibleTasks = tasksAll.filter((t: BriefTask) => {
    const key = dismissKey(t.conversationId, t.briefingItemId);
    if (key && dismissed.has(key)) return false;
    if (t.stream === 'conflict' && outcomeClears(conflictOutcomes.get(t.briefingItemId ?? ''))) return false;
    return true;
  });

  // One flat priority list (Teddy 2026-07-27): conflicts lead (the prototype's rose cards), then
  // everything else in server value-order — no "Today's focus" / "Exact to-dos" lanes. Top
  // priority is just the high-priority items, whether actions or things to read.
  // Phase-2 caps (Teddy 2026-07-26): the SERVER enforces ≤3 non-conflict tasks inline
  // (TASKS_INLINE_BUDGET, MVP constants.ts) — everything received renders. The old
  // "Show all N" expander is gone: overflow is a problem, not an expander; cut tasks
  // live in the distillation panels below, not behind a click.
  // Conflicts used to be EXEMPT from every cap ("a conflict can't fall off the brief"), so a busy team
  // buried the priority column under conflict cards and nothing else could be seen. The server now
  // escalates at most 2 and flags the rest `escalated:false`. Demotion is not suppression: the demoted
  // ones still arrive in `tasks`, still render in full on Actions, and are counted in the rollup below
  // — never silently dropped. `!== false` keeps legacy payloads (no field) rendering exactly as before.
  const allConflicts = visibleTasks.filter((t: BriefTask) => t.stream === 'conflict');
  // Demote only a conflict that HAS a home to be demoted to: ActionsSurface lists conflicts by
  // briefingItemId, so one without an id (its briefing_items insert failed) is invisible there — hiding
  // it here too would leave it rendered nowhere, pointed at by a rollup that can't deliver it. Keeping
  // its brief slot is the safe direction: visible-but-unresolvable beats gone.
  const conflictTasks = allConflicts.filter((t: BriefTask) => t.escalated !== false || !t.briefingItemId);
  const shownTasks = visibleTasks.filter((t: BriefTask) => t.stream !== 'conflict');
  // The count is DERIVED from what this render withheld, so it cannot disagree with the column. Not
  // sections.conflictsDemotedCount: that number predates the reader's own session dismissals (settle a
  // demoted conflict on Actions and it would keep advertising it), and an undercount there would hide
  // rows with no receipt at all. The server field stays on the contract for the Slack DM; the web
  // states what it did.
  const demotedConflictCount = allConflicts.length - conflictTasks.length;

  // B3 item 3: the ⚠️ spotlight axis. task-streams.ts flags every high-band conflict, but the Slack
  // DM only ever spotlights ONE (the head of its visible window) — a "look here first", not a badge
  // on half the column. Mirror that: the first card in render order carrying the flag, and only it.
  const spotlightTaskId = [...conflictTasks, ...shownTasks].find((t: BriefTask) => t.spotlight === true)?.taskId ?? null;

  const priorityCount = conflictTasks.length + shownTasks.length;
  const onItemExpanded = (itemId: string, type: string) => track('item_expanded', { item_id: itemId, type });
  const onFlagWrong = (itemId: string, reason: FlagWrongReason) =>
    track('item_flagged_wrong', { item_id: itemId, reason_code: reason });
  const readThroughItemsSurfaced = [
    ...conflictTasks.map(task => task.briefingItemId ?? task.conversationId),
    ...shownTasks.map(task => task.briefingItemId ?? task.conversationId),
    ...bandCards.map(card =>
      card.kind === 'change'
        ? card.change.conversationId
        : card.rel.briefingItemId ?? card.rel.conversationId,
    ),
  ].filter((itemId): itemId is string => Boolean(itemId)).length;
  // Static section sizing (Teddy 2026-07-27): the column always holds 3 slots — real cards first,
  // skeleton fallbacks for the rest — so the calendar column has a stable height to match.
  const priorityGhosts = Math.max(0, 3 - priorityCount - (priorityCount === 0 ? 1 : 0));

  const navNote = (conversationId: string) => onNav('conversations/' + conversationId);

  // PR-3: a CONSOLIDATED item's story object has one MEMBER NOTE per place it showed up, so the
  // dialog lists one row per citation instead of a single synthesized author row — each with its
  // own source link when the artifact is derivable (url null → the row still offers the note nav,
  // which is the designed fallback, never a dead link). Dates ride relativeTime(_, readOnly) for
  // the same reason the card faces do: an archived brief must not re-date itself against now.
  //
  // The row list is scoped to >1 citation (D-4: "the CONSOLIDATED card's dialog"). A singleton
  // object has nothing to consolidate, and its one citation's `label` IS the headline the reader
  // just clicked — so the dialog echoed its own title and dropped the author name entirely. The
  // singleton branch below restores the author row and grafts the citation's source-first href onto
  // it, so the fix does not cost the direct artifact link.
  //
  // B3 item 4: each row also states its member note's VISIBILITY class when it is not the default
  // `public` — the payload has carried it since PR-3 and no reader ever read it, so a note the
  // reader can see only because it was shared with them (or because it is their own private note)
  // looked identical to a whole-team one.
  const citationRows = (citations: BriefCitation[] | null | undefined, fallbackTitle: string) =>
    (citations ?? []).map(c => ({
      title: c.label || fallbackTitle,
      note:
        [
          c.site ? siteLabel(c.site) : null,
          c.timestamp ? relativeTime(c.timestamp, readOnly) : null,
          visibilityLabel(c.visibility),
        ]
          .filter(Boolean)
          .join(' · ') || null,
      href: c.url ?? null,
      conversationId: c.conversationId,
    }));

  /*
   * The SELF-ECHO filter (bug-575, r2). A card's citation list has always included the card's OWN
   * note, so a two-member story rendered its headline note twice — once as the dialog's title and
   * again as the first "member note" row — and a one-member story rendered a row that was purely
   * its own title. The MVP now stops emitting that row, but every brief already stored still
   * carries it, so the drop happens HERE, and it happens BY IDENTITY (conversationId), not by
   * count: an identity filter is a no-op on the clean payload and a correct fix on the old one.
   *
   * That is also why the block's render condition below is `> 0` and no longer `> 1`. The old
   * count test existed only to hide the lone self-echo of a singleton card; against a clean payload
   * it would instead hide the ONE row a two-member card legitimately has left.
   */
  const otherCitations = <T extends { conversationId: string }>(rows: T[], selfConversationId: string | null): T[] =>
    selfConversationId ? rows.filter(r => r.conversationId !== selfConversationId) : rows;

  // Ask hands the item to the one Ask surface through AskLauncher's own contract: /ask?q= prefills
  // and focuses the composer, and NEVER auto-submits (a 20-45s turn must not fire from a URL).
  const askAbout = (prefill: string) => onNav(askNavTarget(prefill));

  // The universal exit set (ruling C1): [Open the note] + [Open in source ↗] + [Ask]. The rules and
  // the wording live in lib/brief-card-exits.ts; this only supplies the card's own facts.
  //
  // `conversationId` is NULLABLE here on purpose — a priority task can arrive ungrounded (the shape
  // behind bug-614). It resolves to a card with no note exit, not to a card with no exits.
  const primaryFor = (opts: {
    citations?: BriefCitation[] | null;
    conversationId: string | null;
    externalUrl?: string | null;
    site?: string | null;
    title: string;
  }): RdCardPrimary => ({
    sourceTargets: opts.conversationId
      ? resolveSourceTargets(opts.citations, opts.conversationId, opts.site)
      : null,
    externalUrl: opts.externalUrl ?? null,
    noteConversationId: opts.conversationId,
    askPrefill: askPrefillFor(opts.title),
    onNavNote: navNote,
    onAsk: askAbout,
  });

  /* Around-card drill-ins (progressive disclosure: card → popup → link out). With a STORED body
   * (sections.popoutBodies — pregeneration build spec §5) the card opens the RULED dialog: the
   * pregenerated {shape, body, facts} renders instantly, citation item rows are gone from the
   * brief-object cards (ruling 3 — the picker is the traceability surface now), attribution sits
   * quietly with the tags, and ONE open button carries the source picker (ruling 9). Without a
   * stored body (legacy briefs, history, tail rows past the pregen cap) the LEGACY dialog below
   * runs byte-identical — per-click POST compose + citation rows — until PR C deletes it. */
  const attributionFor = (name: string, site: string | null, timestamp: string | null, extra?: string | null) =>
    [
      `From ${name}`,
      site ? siteLabel(site) : null,
      timestamp ? relativeTime(timestamp, readOnly) : null,
      extra ?? null,
    ]
      .filter(Boolean)
      .join(' · ');
  // Ruling 2 is a PRECEDENCE rule (composed lede > description fallback), so the branch keys on a
  // body that will actually RENDER, not on mere key presence: an entry whose body is blank would
  // otherwise suppress the description and open a dialog with no prose at all (review finding C3).
  // Blank bodies are contract-defensive — the MVP's sanitize/fallback path always writes one.
  const storedBodyFor = (conversationId: string | null | undefined) => {
    const stored = conversationId ? sections.popoutBodies?.[conversationId] : undefined;
    return stored?.body?.trim() ? stored : undefined;
  };
  /* Phase-4 GAP 2b (Dash half) — the TASK-KEYED body lookup.
   *
   * popoutBodies is keyed by conversationId for note-anchored cards and `panel:<source>:<topicId>`
   * for panel topics. Two task streams carry no conversationId at all — meeting_prep and linear
   * both hardcode `conversationId: null` in MVP task-streams.ts — so selectPopoutCandidates' push()
   * guard silently skipped them and their cards opened a dialog with an EMPTY body zone (title +
   * the task's own one-line detail + the exits). That is the "action items do not have a popout
   * with generated description" report.
   *
   * The MVP side now emits a third key form, `task:<taskId>`, for a non-conflict task that has an
   * id but no note. The lookup is deliberately NOT stream-gated here: whatever the pass decides to
   * key, a card that finds a body renders it, so a widening on that side needs no Dash change.
   * Same blank-body defensiveness as storedBodyFor (review finding C3's rule). */
  const storedTaskBodyFor = (taskId: string | null | undefined) =>
    storedBodyFor(taskId ? `task:${taskId}` : undefined);
  const openChangeCard = (c: WhatChangedItem) => {
    const title = c.headline || c.text || c.topic;
    const storedChange = storedBodyFor(c.conversationId);
    if (storedChange) {
      return setOpenObjectCard({
        title,
        chips: [catLabel(c.category), ...(c.changeType ? [c.changeType] : [])],
        attribution: attributionFor(
          (c.people && c.people.length > 0 ? c.people : [c.memberName]).join(' & '),
          c.site,
          c.timestamp,
          restrictedProvenance(c.fromRestricted, c.site),
        ),
        // Ruling 2 — ONE lede slot: the stored body's first sentence takes the lede register;
        // the card description does not render here.
        lede: null,
        body: <StoredPopoutBodyView stored={storedChange} />,
        openControl: briefObjectOpenControl({
          targets: resolveSourceTargets(c.citations, c.conversationId, c.site, c.sourceRefs),
          conversationId: c.conversationId,
          siteLabelOf: siteLabel,
        }),
        askPrefill: askPrefillFor(title),
        onNavNote: navNote,
        onAsk: askAbout,
      });
    }
    // `allCited` keeps the raw list because the singleton fallback row below grafts the citation's
    // artifact href onto the author row — and on a self-echoing payload that citation IS the one the
    // filter drops. `cited` is what actually renders.
    const allCited = citationRows(c.citations, title);
    const cited = otherCitations(allCited, c.conversationId);
    const tag = c.changeType ?? catLabel(c.category);
    return setOpenCard({
      icon: <FileText size={19} />,
      eyebrow: `${catLabel(c.category)}${c.changeType ? ` · ${c.changeType}` : ''}`,
      title,
      // The note's own summary is the richer expand line; `why` is the terse card-face one and
      // stays the fallback for briefings generated before PR-3.
      description: c.summary ?? c.why ?? null,
      body: (
        <BriefPopoutBody
          conversationId={c.conversationId}
          title={title}
          tag={tag}
          why={c.why ?? null}
          // The dialog renders max(cited.length, 1) item rows (single-row fallback below); the
          // server's one-screen budget subtracts the citation block by row count (bug-612). This is
          // the TRUE post-filter count, uncapped: the cap is the server's to charge (it holds
          // CITATION_ROWS_DISPLAY_CAP), and a number pre-capped here would hide the rest of the
          // list from the budget entirely.
          citationCount={Math.max(cited.length, 1)}
        />
      ),
      items:
        cited.length > 0
          ? cited
          : [
              {
                title: (c.people && c.people.length > 0 ? c.people : [c.memberName]).join(' & '),
                note: [
                  siteLabel(c.site),
                  relativeTime(c.timestamp, readOnly),
                  restrictedProvenance(c.fromRestricted, c.site),
                ]
                  .filter(Boolean)
                  .join(' · '),
                tag,
                href: allCited[0]?.href ?? null,
                conversationId: allCited[0]?.conversationId ?? c.conversationId,
              },
            ],
      itemsCap: CITATION_ROWS_DISPLAY_CAP,
      actions: [],
      primary: primaryFor({ citations: c.citations, conversationId: c.conversationId, site: c.site, title }),
      onNavNote: navNote,
    });
  };
  const openRelevantCard = (r: RelevantToYourWorkItem) => {
    const storedRel = storedBodyFor(r.conversationId);
    if (storedRel) {
      return setOpenObjectCard({
        title: r.topic,
        chips: [r.variant === 'solution' ? 'Solution' : 'Similar approach', confidenceLabel(r.confidenceBand)],
        attribution: attributionFor(r.authorName, r.site ?? null, r.timestamp ?? null),
        lede: null,
        body: <StoredPopoutBodyView stored={storedRel} />,
        openControl: briefObjectOpenControl({
          targets: resolveSourceTargets(r.citations, r.conversationId, r.site, r.sourceRefs),
          conversationId: r.conversationId,
          siteLabelOf: siteLabel,
        }),
        askPrefill: askPrefillFor(r.topic),
        onNavNote: navNote,
        onAsk: askAbout,
      });
    }
    const allCited = citationRows(r.citations, r.topic);
    const cited = otherCitations(allCited, r.conversationId);
    return setOpenCard({
      icon: <TrendingUp size={19} />,
      eyebrow: `Relevant · ${confidenceLabel(r.confidenceBand)}`,
      title: r.topic,
      // G2 (2026-08-03): the dedicated relevance explanation line is removed as a class — no
      // description meta-line; the composed popout body carries the card's substance.
      body: (
        <BriefPopoutBody
          conversationId={r.conversationId}
          title={r.topic}
          tag={r.variant === 'solution' ? 'Solution' : 'Similar approach'}
          why={null}
          citationCount={Math.max(cited.length, 1)}
        />
      ),
      items:
        cited.length > 0
          ? cited
          : [
              {
                title: r.authorName,
                note:
                  [r.site ? siteLabel(r.site) : null, r.timestamp ? relativeTime(r.timestamp, readOnly) : null]
                    .filter(Boolean)
                    .join(' · ') || null,
                tag: r.variant === 'solution' ? 'Solution' : 'Similar approach',
                href: allCited[0]?.href ?? null,
                conversationId: allCited[0]?.conversationId ?? r.conversationId,
              },
            ],
      itemsCap: CITATION_ROWS_DISPLAY_CAP,
      actions: [],
      primary: primaryFor({ citations: r.citations, conversationId: r.conversationId, site: r.site, title: r.topic }),
      onNavNote: navNote,
    });
  };

  /* The priority column's ONE drill-in (ruling C1). Every non-conflict task opens this — the same
   * composed panel, the same exits — where the column used to hold three different depths: overlap
   * popped out, a grounded focus task jumped straight to its note, and an UNGROUNDED one had no
   * verb at all (bug-614). Flattening removes the dead end by construction: the panel always
   * carries [Ask], which needs no ids.
   *
   * The overlap block (walk D15) is a SECTION of this panel, not a second function. The join is
   * client-side and needs no payload change: an overlap task's conversationId IS the needsYourInput
   * item's conversationId (both memberAConvId), so sharedEntities, the open PR's conflicting file,
   * branch divergence and the OTHER person's conversation still render exactly where they did.
   *
   * A task with no note falls back to the TASK-KEYED body (GAP 2b) and, failing that, to its title,
   * its detail and its exits. Smaller, never dead. */
  const openTaskCard = (task: BriefTask) => {
    /* GAP 2a — a meeting_prep card opens the PREP DOCUMENT, not a dialog.
     *
     * This card had the emptiest body zone on the brief (its stream hardcodes conversationId: null,
     * so nothing composed for it) while the SAME meeting, reached from the day rail's Prep button,
     * opens MeetingPrepModal — a live two-request grounded read with real prose. The material
     * already existed; only this card could not reach it. Short-circuited BEFORE the body lookups
     * on purpose: the modal's live grounding is strictly richer than any pregenerated body would
     * be, which is exactly why meetings were carved out of the pregen pass (spec §10).
     *
     * readOnly is honoured the same way the rail honours it (`onPrep={readOnly ? undefined : …}`):
     * the modal reads LIVE calendar + query-engine state, so on an archived brief it would answer
     * about today for a meeting that happened weeks ago. An archived meeting_prep card therefore
     * keeps the dialog below — frozen "as generated", per the temporal-mismatch law. */
    if (task.stream === 'meeting_prep' && !readOnly) {
      const meeting = meetingByTaskId.get(task.taskId);
      if (meeting) return setPrepMeeting(meeting);
    }
    const conversationId = task.conversationId ?? null;
    const item = conversationId ? needsByConversation.get(conversationId) : undefined;
    const why = task.detail ?? item?.detail ?? null;
    // Note-keyed body first, then the task-keyed one (GAP 2b): a task that HAS a note keeps
    // composing from the note, so the widening can only add coverage, never redirect it.
    const storedTask = storedBodyFor(conversationId) ?? storedTaskBodyFor(task.taskId);
    if (storedTask) {
      // Ruled dialog for a pregenerated task card. Task `detail` KEEPS its slot (spec §5.1's
      // audit constraint) — it rides as the lede line above the stored body; the overlap
      // section stays a section of the same body zone (walk D15's join, unchanged).
      const overlapDetail =
        task.stream === 'overlap' ? (
          <OverlapDetail
            sharedEntities={item?.sharedEntities}
            branchDivergence={item?.branchDivergence}
            secondaryConversationId={item?.secondaryConversationId}
            onNavNote={navNote}
          />
        ) : null;
      return setOpenObjectCard({
        title: task.title,
        chips: [STREAM_LABELS[task.stream] ?? task.stream],
        attribution: null,
        lede: why,
        body: (
          <>
            <StoredPopoutBodyView stored={storedTask} />
            {overlapDetail}
          </>
        ),
        openControl: briefObjectOpenControl({
          // Ruling-7 widening (spec §12): tasks now carry sourceRefs, so a pregenerated task
          // card's picker gets its PR rows + the guaranteed note row instead of a bare note exit.
          targets: conversationId ? resolveSourceTargets(null, conversationId, null, task.sourceRefs) : [],
          conversationId,
          externalUrl: task.externalUrl ?? null,
          siteLabelOf: siteLabel,
        }),
        askPrefill: askPrefillFor(task.title),
        onNavNote: navNote,
        onAsk: askAbout,
      });
    }
    const Icon = STREAM_ICON[task.stream] ?? Clock3;
    const composed = conversationId ? (
      <BriefPopoutBody
        conversationId={conversationId}
        title={task.title}
        tag={STREAM_LABELS[task.stream] ?? task.stream}
        why={why}
      />
    ) : null;
    const overlap =
      task.stream === 'overlap' ? (
        <OverlapDetail
          sharedEntities={item?.sharedEntities}
          branchDivergence={item?.branchDivergence}
          secondaryConversationId={item?.secondaryConversationId}
          onNavNote={navNote}
        />
      ) : null;
    return setOpenCard({
      icon: <Icon size={19} />,
      eyebrow: STREAM_LABELS[task.stream] ?? task.stream,
      title: task.title,
      description: why,
      // A Fragment, not a wrapper div: the dialog collapses its body zone with `:empty`, and an
      // unconditionally-rendered wrapper is a child, so a zone holding only an empty wrapper would
      // never collapse. The 20px between the two blocks moves onto OverlapDetail's own root.
      body:
        composed || overlap ? (
          <>
            {composed}
            {overlap}
          </>
        ) : undefined,
      actions: [],
      primary: primaryFor({
        conversationId,
        externalUrl: task.externalUrl ?? null,
        site: null,
        title: task.title,
      }),
      onNavNote: navNote,
    });
  };
  /* D5/D14 (B2) — the General section's "N more" opens the SAME card → popout pattern as everything
   * else on the brief: a sheet of rows, one per item, each a heading + body + chip, each opening its
   * note. It is not an inline expander, and it is not the Map: the Map was the old receipt precisely
   * because the cut set wasn't guaranteed to be readable anywhere: this list IS the cut set. Every
   * string it renders is the vocabulary the band cards already use — the section eyebrow, the
   * section heading, catLabel/confidenceLabel, siteLabel — so the expansion reads as the section it
   * came from rather than a new register. */
  /* The sheet's COLLAPSED-PREVIEW height, not a reachability cap (phase-4 GAP 1).
   *
   * B1 interim (2026-08-02) cut this to stop a 20-row / 4.72-screen sheet, back when a tail row was
   * a receipt you looked at: it sliced the list and offered "+N more · on your Map" for the rest.
   * Spec §12 then made every tail row a DOOR (a row with a stored body opens the ruled dialog with
   * that body) without revisiting the slice — so the cap silently became the only thing standing
   * between the reader and 9 of the 35 composed bodies on the 2026-08-08 brief. That day the whole
   * General change set had fallen into the tail (SUMMARY_INLINE_BUDGET = 3 went entirely to
   * relevance matches, so around.changes was empty and the pill read "13 more"), and the tail is
   * uncapped server-side, so a busier day strands proportionally more. The Map exit was no second
   * route: it opens a note-node canvas with no brief-body renderer.
   *
   * Now the number only decides how much renders BEFORE one click: RdCardDialog's rowsCap reveals
   * the remainder in place inside a scroll area (the CardItems pattern this dialog already shipped
   * for the 23-row panel lists). The wire still keeps the full tail, so the General button's count
   * is still the length of the list this opens — and now every one of them is openable.
   *
   * It lives in lib/brief-popout-coverage beside the reachability model so the guard can pin it BY
   * NAME and prove the model ignores it. A future cap change then cannot quietly strand payload
   * again without a test saying so.
   */
  /* Spec §12 (every-section widening): a tail row with a stored body opens the RULED dialog —
   * the renderer slot the original audit said didn't exist is exactly this. The sheet closes so
   * the row's dialog replaces it (one overlay at a time); a row without a body keeps today's
   * direct note nav. */
  const openTailRow = (row: BriefAroundTailItem) => {
    const stored = storedBodyFor(row.conversationId);
    if (!stored) return navNote(row.conversationId);
    setOpenCard(null);
    const chip =
      row.kind === 'change'
        ? row.changeType ?? (row.category ? catLabel(row.category) : null)
        : row.confidenceBand
          ? confidenceLabel(row.confidenceBand)
          : null;
    setOpenObjectCard({
      title: row.title,
      chips: chip ? [chip] : [],
      attribution: row.authorName ? attributionFor(row.authorName, row.site ?? null, row.timestamp ?? null) : null,
      lede: null,
      body: <StoredPopoutBodyView stored={stored} />,
      openControl: briefObjectOpenControl({
        targets: resolveSourceTargets(null, row.conversationId, row.site),
        conversationId: row.conversationId,
        siteLabelOf: siteLabel,
      }),
      askPrefill: askPrefillFor(row.title),
      onNavNote: navNote,
      onAsk: askAbout,
    });
  };
  const openTailCard = (rows: BriefAroundTailItem[]) => {
    track('brief_section_expanded', { section: 'overview_more', count: rows.length });
    const rowById = new Map(rows.map(r => [r.conversationId, r]));
    setOpenCard({
      icon: <Layers size={19} />,
      eyebrow: 'General',
      title: 'Worth knowing today',
      description: 'Nothing here appears anywhere else in today’s brief.',
      // The FULL tail goes over (no slice): every row is an opener for a stored body, so a slice
      // here deletes payload. The cap below is a preview height the reader can expand.
      rows: rows.map(row => ({
        title: row.title,
        body: row.body ?? null,
        chip:
          row.kind === 'change'
            ? row.changeType ?? (row.category ? catLabel(row.category) : null)
            : row.confidenceBand
              ? confidenceLabel(row.confidenceBand)
              : null,
        meta:
          [
            row.site ? siteLabel(row.site) : null,
            row.authorName,
            row.timestamp ? relativeTime(row.timestamp, readOnly) : null,
          ]
            .filter(Boolean)
            .join(' · ') || null,
        conversationId: row.conversationId,
      })),
      rowsCap: TAIL_POPOUT_ROW_CAP,
      // No Map tile: the overflow reveals in place now, so the exit that used to stand for it would
      // advertise rows the reader can already reach — at a surface that cannot render their bodies.
      actions: [],
      // The sheet's row click routes through the stored-body branch (spec §12); a row the pregen
      // pass never reached falls through to the note, byte-identical to before.
      onNavNote: (conversationId: string) => {
        const row = rowById.get(conversationId);
        if (row) openTailRow(row);
        else navNote(conversationId);
      },
    });
  };

  /* Spec §12: a panel topic with a stored body (`panel:<source>:<topicId>`) opens the RULED
   * dialog; §5.2's per-item destinations survive as picker rows (rulings 7-8, one destination
   * each: the source url when present, else the item's note). Review fix: the picker rows are
   * built directly — routing url-less topics back to the legacy dialog gave adjacent rows of
   * one panel two different drill-in contracts and left their stored bodies dead payload.
   * Only a topic with no stored body keeps the legacy dialog. Returns true when handled. */
  const openTopicObject = (source: string, topic: BriefSourceTopic): boolean => {
    const stored = storedBodyFor(`panel:${source}:${topic.id}`);
    if (!stored) return false;
    const items = topic.items ?? [];
    const count = topic.count ?? items.length;
    const rows = items
      .map(it => ({
        label: it.title,
        meta: it.url ? `${siteLabel(source)} ↗` : it.conversationId ? 'opens the note' : null,
        href: it.url ?? null,
        conversationId: it.url ? null : (it.conversationId ?? null),
      }))
      .filter(r => r.href || r.conversationId);
    setOpenObjectCard({
      title: topic.title,
      chips: [siteLabel(source), `${count} item${count === 1 ? '' : 's'}`],
      attribution: null,
      lede: null,
      body: <StoredPopoutBodyView stored={stored} />,
      // One control, one contract: 'Open the source' when any row leaves the app, else 'Open
      // the note' with the picker choosing WHICH note (both strings are the gated RULED set).
      // A topic with zero destinations gets no control — Ask + Done remain (bug-614 shape).
      openControl:
        rows.length > 0
          ? { kind: 'source', label: rows.some(r => r.href) ? RULED_EXIT_LABEL.source : RULED_EXIT_LABEL.note, rows }
          : null,
      askPrefill: askPrefillFor(topic.title),
      onNavNote: navNote,
      onAsk: askAbout,
    });
    return true;
  };

  /* Spec §12: a "Decisions forming" card with a stored body becomes clickable and opens the
   * ruled dialog against its representative conversation. Without a body the card stays the
   * inert render it has always been. */
  const openDecisionObject = (d: SharedDecision) => {
    const stored = storedBodyFor(d.conversationId);
    if (!stored) return;
    setOpenObjectCard({
      title: d.decisionQuestion,
      chips: ['Decision forming', ...(d.teamProjectName ? [d.teamProjectName] : [])],
      attribution: null,
      lede: null,
      body: <StoredPopoutBodyView stored={stored} />,
      openControl: briefObjectOpenControl({
        targets: resolveSourceTargets(null, d.conversationId, null),
        conversationId: d.conversationId,
        siteLabelOf: siteLabel,
      }),
      askPrefill: askPrefillFor(d.decisionQuestion),
      onNavNote: navNote,
      onAsk: askAbout,
    });
  };

  /* Phase-4 GAP 4: a standup card ("People in motion") with a stored body for its FIRST
   * conversation opens the ruled dialog, exactly like the coda; without one it keeps today's direct
   * note nav. Before this, the same note opened a composed body when it was reached from one section
   * and a raw note page when it was reached from here — and standup is not a pregen candidate
   * source, so every body it can open was composed for another section anyway. On the 2026-08-08
   * brief that resurrected two of the nine stranded tail bodies for free.
   *
   * NAMED LIMITATION: a standup card summarizes SEVERAL conversations and this opens the FIRST
   * one's body, so the dialog is a partial representation of the card face. That is the coda's
   * precedent and it is the honest half of the trade — the alternative is leaving a composed body
   * unreachable to avoid showing part of a story. */
  const openStandupEntry = (s: StandupEntry) => {
    const conversationId = s.conversationIds?.[0] ?? null;
    if (!conversationId) return;
    const stored = storedBodyFor(conversationId);
    if (!stored) return onNav('conversations/' + conversationId);
    setOpenObjectCard({
      title: s.sentence,
      chips: s.memberRole ? [s.memberRole] : [],
      attribution: attributionFor(s.memberName, null, null),
      lede: null,
      body: <StoredPopoutBodyView stored={stored} />,
      openControl: briefObjectOpenControl({
        targets: resolveSourceTargets(null, conversationId, null),
        conversationId,
        siteLabelOf: siteLabel,
      }),
      askPrefill: askPrefillFor(s.sentence),
      onNavNote: navNote,
      onAsk: askAbout,
    });
  };

  /* Spec §12: a coda (knowledge-captured) item with a stored body opens the ruled dialog; one
   * without keeps the direct note nav. */
  const openCodaItem = (k: KnowledgeCapturedItem) => {
    const stored = storedBodyFor(k.conversationId);
    if (!stored) return onNav('conversations/' + k.conversationId);
    setOpenObjectCard({
      title: k.text,
      chips: [k.type],
      attribution: k.authorName ? attributionFor(k.authorName, k.site ?? null, k.timestamp ?? null) : null,
      lede: null,
      body: <StoredPopoutBodyView stored={stored} />,
      openControl: briefObjectOpenControl({
        targets: resolveSourceTargets(null, k.conversationId, k.site),
        conversationId: k.conversationId,
        siteLabelOf: siteLabel,
      }),
      askPrefill: askPrefillFor(k.text),
      onNavNote: navNote,
      onAsk: askAbout,
    });
  };

  // The calendar renders CHRONOLOGICALLY (the mock's day rail). The payload arrives importance-first
  // (meeting-triage §8 sorts today→tomorrow, importance desc, THEN start time) — right for triage
  // weight, wrong for a timeline: a 2p flagship meeting would render above a 1p one, and the naive
  // "first future meeting" pick for the header tail would name the wrong clock.
  const meetingsByClock = [...meetings].sort(
    (a, b) =>
      (a.day === 'tomorrow' ? 1 : 0) - (b.day === 'tomorrow' ? 1 : 0) ||
      (a.startAt ?? '').localeCompare(b.startAt ?? ''),
  );

  // Deterministic calendar tail — the mock's "Your day · free until 10" register, computed from the
  // payload's own meetings (no LLM). "free until X" is a claim about the reader's NOW, so it only
  // renders on the live brief for the CHRONOLOGICALLY next meeting still ahead; archived briefs fall
  // back to the plain count.
  // bug-539 (Teddy 2026-07-27): the band shows TODAY only — a tomorrow meeting under "Your day ·
  // clear today" read as a contradiction. Tomorrow's triage stays in the payload for the Slack DM.
  const todays = meetingsByClock.filter(m => m.day !== 'tomorrow');
  const nextToday = readOnly
    ? undefined
    : todays.find(m => m.startAt && new Date(m.startAt).getTime() > Date.now());
  const nextClock = nextToday ? formatClock(nextToday.startAt) : null;
  const dayTail =
    todays.length === 0
      ? 'clear today'
      : nextClock
        ? `free until ${nextClock}`
        : `${todays.length} meeting${todays.length === 1 ? '' : 's'} today`;

  // Full-day rail bookends (Teddy 2026-07-28): a free row before the first hold and after the
  // last one, when the working-day frame leaves a real (≥45 min) stretch. A missing start/end
  // timestamp renders no bookend — never a guessed one.
  const firstStartMin = localMinutes(todays[0]?.startAt);
  const leadingFree = firstStartMin != null && firstStartMin - DAY_START_HOUR * 60 >= FREE_ROW_MIN_GAP;
  const lastMeeting = todays.length > 0 ? todays[todays.length - 1] : null;
  const lastEndMin = localMinutes(lastMeeting?.endAt);
  const trailingFree = lastEndMin != null && DAY_END_HOUR * 60 - lastEndMin >= FREE_ROW_MIN_GAP;

  // The rail's rows, each stamped with its local start-minute so the live now-line can slot
  // itself after the last row already underway. (The weather footer is gone — Teddy 2026-07-28;
  // the masthead tile owns weather.)
  const railRows: { startMin: number; node: JSX.Element }[] = [];
  if (todays.length > 0) {
    if (leadingFree) {
      railRows.push({ startMin: DAY_START_HOUR * 60, node: <RdFreeRow key="lead" clock={hourClock(DAY_START_HOUR)} /> });
    }
    todays.forEach((m, i) => {
      if (i > 0 && freeGapAfter(todays[i - 1], m)) {
        railRows.push({
          startMin: localMinutes(todays[i - 1].endAt) ?? (localMinutes(m.startAt) ?? 0) - 1,
          node: <RdFreeRow key={`gap-${i}`} clock={formatClock(todays[i - 1].endAt) ?? ''} />,
        });
      }
      railRows.push({
        startMin: localMinutes(m.startAt) ?? 0,
        node: (
          <RdCalendarRow
            key={m.eventId || `mtg-${i}`}
            meeting={m}
            frozen={readOnly}
            onPrep={readOnly ? undefined : () => setPrepMeeting(m)}
          />
        ),
      });
    });
    if (trailingFree) {
      railRows.push({ startMin: lastEndMin!, node: <RdFreeRow key="trail" clock={formatClock(lastMeeting!.endAt) ?? ''} /> });
    }
  } else {
    // Clear day: the rail still spans the day — morning, midday, afternoon free stretches.
    for (const h of [9, 12, 15]) {
      railRows.push({ startMin: h * 60, node: <RdFreeRow key={`free-${h}`} clock={hourClock(h)} /> });
    }
  }
  // The now-line sits after the last row already underway; archived briefs get none.
  const nowIdx = readOnly ? null : railRows.filter(r => r.startMin <= nowMin).length;

  // ---- Masthead greeting (§B5 / walk D10): deterministic, LLM-free, and keyed ONLY off signals
  // this render already shows, so the opening line can never contradict the page under it.
  // `waiting` and `quiet` read the POST-dismiss values on purpose: settle the last conflict and
  // the masthead stops claiming there is one. Selection itself lives in lib/brief-greeting.ts.
  const greeting = pickGreeting({
    readerId,
    briefDate: briefDateKey(data.generated_at),
    signals: {
      // coverage is null for a legacy row / no team — unknown is not solo.
      solo: !!sections.coverage && sections.coverage.connected <= 1,
      // The zero-signal disjunct counts EVERY card class the page can show (review F8): a day with
      // three relevant cards and no tasks is not a day where "almost nothing came in". The server's
      // own quietDay flag stays a trigger, but it is computed from tasks+around alone, which is why
      // greetingBucket ranks quiet below the shapes it cannot see (meetings, conflicts).
      quiet:
        sections.quietDay === true ||
        (visibleTasks.length === 0 && changes.length === 0 && relevant.length === 0),
      // The same number the tasks column prints as "+N below today's threshold".
      heavy: tasksCollapsedCount + tasksOverflowCount > 0,
      waiting: conflictTasks.length > 0,
      meetingDense: todays.length >= MEETING_DENSE_MIN,
    },
    // The overlay keys on the reader's clock, which only the CURRENT day's brief can claim:
    // an archived row (readOnly) and a stood-in previous day (is_previous) render pure day-shape.
    isLive: !readOnly && data.is_previous !== true,
    // FROZEN per loaded brief (review F10) — a fresh `new Date()` here would re-evaluate on every
    // re-render (the rail's minute tick, a dismiss, a dialog), so a reader sitting on the page at
    // 22:00 would watch the masthead swap to "Hey, night owl." under them.
    now: greetingClock,
  });

  return (
    <BriefReadTracker
      briefDay={briefDateKey(data.generated_at)}
      enabled={data.status === 'ready'}
      itemsSurfaced={readThroughItemsSurfaced}
    >
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground selection:bg-[#d9b298] selection:text-[#3e2f2a]">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        {/* ---- Top strip: crumb + history/regenerate ---- */}
        <div className="flex items-center justify-between border-b border-border pb-3">
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">
            {formatCrumb(data.generated_at)}
            {data.is_previous && ' · yesterday'}
          </span>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <RegenerateButton className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground" />
            )}
            <button
              type="button"
              className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground"
              onClick={() => onNav('briefing-history')}
            >
              {readOnly ? '← Past briefings' : 'Past briefings →'}
            </button>
          </div>
        </div>

        {/* ---- D3 freshness banner (decision walk 2026-08-12): ONE banner, TWO states, shown only
          * when yesterday's brief is standing in for today's. Building: the D1 auto-kick is in
          * flight and the poll will swap the fresh brief in. Stuck: the kick's window lapsed (or
          * the daily cap was already spent / the kick failed), so the reader gets the manual
          * Build-now path. Copy is DRAFT pending Teddy's sign-off (standing rule) — flagged in the
          * PR. Never rendered readOnly: history rows are frozen "as generated". */}
        {!readOnly && data.is_previous && (
          <div
            data-testid="brief-freshness-banner"
            className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
          >
            {data.refresh_in_flight ? (
              <span className="inline-flex items-center gap-2">
                <span className="brief-regen-spin" aria-hidden />
                {"Showing yesterday's brief. Building today's now…"}
              </span>
            ) : (
              <>
                <span>{"This is yesterday's brief."}</span>
                <RegenerateButton
                  label="Build today's"
                  busyLabel="Building…"
                  className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground"
                />
              </>
            )}
          </div>
        )}

        {/* ---- Masthead ---- */}
        <section className="grid grid-cols-1 gap-5 py-7 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,.8fr)]">
          <div>
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.2em] text-muted-foreground">
              {formatDateEyebrow(data.generated_at)}
            </p>
            {/* Masthead greeting (§B5, copy approved 2026-07-31): one line from a curated set,
              * PICKED not composed — the selection is deterministic and LLM-free (lib/brief-greeting.ts),
              * so it costs no tokens and can never contradict the page below it. It replaced the single
              * static pair the 2026-07-26 mock-alignment pass installed; the payload's LLM masthead
              * fields still arrive and are still ignored here (the Slack DM renders those). */}
            <h1
              data-testid="brief-greeting"
              className="mt-2 font-['Instrument_Serif'] text-[clamp(42px,5vw,72px)] font-normal leading-[.92] tracking-[-.025em]"
            >
              {greeting.headline}
            </h1>
            <p data-testid="brief-standfirst" className="mt-5 max-w-xl text-[15px] leading-6 text-muted-foreground">
              {greeting.standfirst}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 self-end">
            {sections.weather && <RdWeatherTile weather={sections.weather} />}
            <RdHealthTile />
          </div>
        </section>

        {/* Ghost Phase B (§3.1): one honest coverage line for a solo recipient. */}
        {sections.coverage && sections.coverage.connected <= 1 && sections.coverage.observed >= 1 && (
          <div className="mb-5 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            Team sections need teammates: {sections.coverage.connected} connected, {sections.coverage.observed} observed.
            Invite them to unlock them.
          </div>
        )}

        {/* ---- Your day (calendar) + Top priority — equal-height row (Teddy 2026-07-27):
          * the priority column always holds 3 slots and the calendar card stretches to match. ---- */}
        <section className="grid grid-cols-1 items-stretch gap-5 xl:grid-cols-[minmax(0,1.16fr)_minmax(0,.84fr)]" id="band-yourday">
          <section className="flex h-full flex-col rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Calendar</p>
                <h2 className="mt-1 text-xl font-semibold">
                  Your day <span className="font-normal text-muted-foreground">{dayTail}</span>
                </h2>
              </div>
              <span className="grid size-9 place-items-center rounded-xl bg-muted text-[#766b59] dark:text-muted-foreground">
                <Clock3 size={17} />
              </span>
            </div>
            {/* The rail fills the card (flex-1; free rows stretch) and carries the live now-line. */}
            <div className="flex flex-1 flex-col">
              {railRows.map((r, i) => (
                <Fragment key={`slot-${i}`}>
                  {nowIdx === i && <RdNowLine clock={minuteClock(nowMin)} />}
                  {r.node}
                </Fragment>
              ))}
              {nowIdx === railRows.length && <RdNowLine clock={minuteClock(nowMin)} />}
            </div>
          </section>

          <section id="band-tasks">
            <div className="mb-3 flex items-end justify-between">
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Top priority</p>
                <h2 className="mt-1 text-xl font-semibold">Before the day moves</h2>
              </div>
              {priorityCount > 0 && (
                <span className="rounded-full bg-[#ead5c7] px-2.5 py-1 font-['DM_Mono'] text-[10px] text-[#8c5040] dark:bg-muted dark:text-muted-foreground">
                  {priorityCount} open
                </span>
              )}
            </div>
            <div className="space-y-2.5">
              {conflictTasks.map((t: BriefTask) => (
                <RdConflictCard
                  key={t.taskId}
                  task={t}
                  onResolve={openResolve}
                  onDismiss={dismissConflict}
                  onFlagWrong={onFlagWrong}
                  readOnly={readOnly}
                  spotlight={t.taskId === spotlightTaskId}
                />
              ))}
              {/* The COUNT renders even on an archived brief — withholding cards there while saying
                  nothing would be the same silent cut, just in the past tense. Only the link is
                  suppressed: "→ Actions" is a present-tense action, and a historical brief's demoted
                  conflicts may since have been settled. */}
              {demotedConflictCount > 0 && (
                <p className="text-sm text-muted-foreground" data-testid="conflicts-demoted-rollup">
                  {demotedConflictCount} {conflictTasks.length > 0 ? 'more ' : ''}open conflict
                  {demotedConflictCount === 1 ? '' : 's'}
                  {!readOnly && (
                    <>
                      {' '}
                      <button onClick={() => onNav('actions')} className="text-acc underline-offset-2 transition hover:underline">
                        → Actions
                      </button>
                    </>
                  )}
                </p>
              )}
              {shownTasks.map((task: BriefTask) => (
                <RdTaskCard
                  key={task.taskId}
                  task={task}
                  onDismiss={dismissTask}
                  onItemExpanded={onItemExpanded}
                  onFlagWrong={onFlagWrong}
                  readOnly={readOnly}
                  spotlight={task.taskId === spotlightTaskId}
                  onOpen={() => openTaskCard(task)}
                />
              ))}
              {/* "Nothing needs a call from you" is a lie the moment conflicts were withheld — the
                  rollup below is then the only true statement in the column. */}
              {priorityCount === 0 && demotedConflictCount === 0 && (
                <div className="rounded-[19px] border border-dashed border-[#ddd2c0] bg-card/60 p-4 text-sm text-muted-foreground dark:border-border">
                  Nothing needs a call from you right now.
                </div>
              )}
              {Array.from({ length: priorityGhosts }, (_, i) => (
                <div
                  key={`ghost-${i}`}
                  className="rounded-[19px] border border-dashed border-[#ddd2c0] bg-card/40 p-4 dark:border-border"
                  data-testid="priority-ghost"
                  aria-hidden="true"
                >
                  <div className="flex items-start gap-3">
                    <span className="size-10 shrink-0 rounded-xl bg-muted/60" />
                    <span className="min-w-0 flex-1 space-y-2 pt-1">
                      <span className="block h-3 w-2/3 rounded bg-muted/60" />
                      <span className="block h-3 w-1/3 rounded bg-muted/40" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {tasksCollapsedCount + tasksOverflowCount > 0 && (
              <div className="mt-3 text-sm text-muted-foreground">
                +{tasksCollapsedCount + tasksOverflowCount} below today&rsquo;s threshold
              </div>
            )}
          </section>
        </section>

        {/* ---- General (the "worth knowing" purple band) ---- */}
        {/* B2: a day whose whole General set sits below the admission floor renders no inline card
          * but still HAS a tail — dropping the section then would lose the only receipt those items
          * get ("the important thing is to not miss anything"). */}
        {(changes.length > 0 || relevant.length > 0 || suppressedCount > 0 || (generalTail?.length ?? 0) > 0) && (
          <section
            id="band-around"
            className="mt-5 rounded-[24px] border border-[#2a2447] bg-[#322b4d] p-5 text-[#fbf5e9] shadow-[0_16px_34px_rgba(74,58,107,.12)] sm:p-6"
          >
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
              <div>
                {/* D14: each section earns its slot by OWNING a kind of material; this one owns what
                  * has no other home — inline AND expanded. "General" is that said plainly;
                  * "High-level summary" described a ranked digest of everything, which it is not.
                  * The heading below still holds under the narrower definition. */}
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-[#c7c2d4]">General</p>
                <h2 className="mt-1 font-['Instrument_Serif'] text-[34px] leading-8 text-white">
                  {changes.length + relevant.length > 0
                    ? 'Worth knowing today'
                    : 'Nothing rose above the fold around your work'}
                </h2>
              </div>
            </div>
            {bandCards.length > 0 && (
              <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {bandCards.map((bc, i) =>
                  bc.kind === 'change' ? (
                    <RdAroundCard
                      key={bc.change.conversationId || `wc-${i}`}
                      index={i}
                      eyebrow={
                        bc.change.companyNews
                          ? 'Company news'
                          : `${catLabel(bc.change.category)}${bc.change.changeType ? ` · ${bc.change.changeType}` : ''}`
                      }
                      title={bc.change.headline || bc.change.text || bc.change.topic}
                      body={bc.change.why ?? null}
                      meta={[
                        (bc.change.people && bc.change.people.length > 0 ? bc.change.people : [bc.change.memberName]).join(' & '),
                        relativeTime(bc.change.timestamp, readOnly),
                        // B3 item 4: WHY a restricted row is in this reader's brief — the same
                        // provenance the Slack DM has stated since DEC-1, on the card face.
                        restrictedProvenance(bc.change.fromRestricted, bc.change.site),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      diffstat={bc.change.diffstat ?? null}
                      itemId={bc.change.conversationId}
                      itemType="change"
                      onItemExpanded={onItemExpanded}
                      onFlagWrong={onFlagWrong}
                      onOpen={() => openChangeCard(bc.change)}
                      onDismiss={readOnly ? undefined : () => dismissAround(bc.change.conversationId)}
                    />
                  ) : (
                    <RdAroundCard
                      key={bc.rel.conversationId || `rel-${i}`}
                      index={i}
                      eyebrow={
                        bc.rel.companyNews
                          ? 'Company news'
                          : `Relevant · ${confidenceLabel(bc.rel.confidenceBand)}`
                      }
                      title={bc.rel.topic}
                      // All-cards subdesc: the note's own standfirst — the same body slot the change
                      // card fills with `why`. Absent on legacy briefings → the face stays title-only.
                      body={bc.rel.subdesc ?? null}
                      meta={[
                        bc.rel.site ? `${siteLabel(bc.rel.site)} · ${bc.rel.authorName}` : bc.rel.authorName,
                        bc.rel.timestamp ? relativeTime(bc.rel.timestamp, readOnly) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      topMatch={bc.rel.confidenceBand === 'high'}
                      itemId={bc.rel.briefingItemId ?? bc.rel.conversationId}
                      itemType="relevant"
                      onItemExpanded={onItemExpanded}
                      onFlagWrong={onFlagWrong}
                      onOpen={() => openRelevantCard(bc.rel)}
                      onDismiss={
                        readOnly ? undefined : () => dismissAround(bc.rel.conversationId, bc.rel.briefingItemId)
                      }
                    />
                  ),
                )}
              </div>
            )}
            {/* D5: the receipt is now the EXPANSION, and its number is the length of the list it
              * opens — the old `changesCollapsedCount + relevantCollapsedCount` was a subtraction
              * taken beside the list, so it counted items no surface could reach and items three
              * other sections were already showing. Legacy briefings (no `tail` on the wire) keep
              * the old line: their cut set was never captured, and the Map is still true of every
              * note. */}
            {generalTail
              ? generalTail.length > 0 && (
                  <button
                    type="button"
                    data-testid="general-tail-open"
                    className="mt-4 flex items-center gap-1 rounded-full border border-white/15 bg-white/[.06] px-3 py-1.5 text-sm text-[#c7c2d4] transition hover:bg-white/[.12] hover:text-white"
                    onClick={() => openTailCard(generalTail)}
                  >
                    {`${generalTail.length} more`} <ChevronRight size={13} />
                  </button>
                )
              : changesCollapsedCount + relevantCollapsedCount > 0 && (
                  <button
                    type="button"
                    className="mt-4 flex items-center gap-1 rounded-full border border-white/15 bg-white/[.06] px-3 py-1.5 text-sm text-[#c7c2d4] transition hover:bg-white/[.12] hover:text-white"
                    onClick={() => onNav('map')}
                  >
                    {`+${changesCollapsedCount + relevantCollapsedCount} more · on your Map`}{' '}
                    <ChevronRight size={13} />
                  </button>
                )}
            {/* A5: the honest suppression receipt — what the scorer weighed and cut, and why. */}
            {suppressed && suppressedCount > 0 && (
              <div className="mt-4 border-t border-white/10 pt-3">
                <button
                  type="button"
                  className="brief-suppress-toggle flex items-center gap-1.5 text-sm text-[#c7c2d4] transition hover:text-white"
                  aria-expanded={suppressOpen}
                  aria-controls="brief-suppress-list"
                  onClick={() => setSuppressOpen(v => {
                    if (!v) track('brief_section_expanded', { section: 'suppression_receipt', count: suppressedCount });
                    return !v;
                  })}
                >
                  {suppressedCount} more didn’t make today’s brief
                  <span aria-hidden="true">{suppressOpen ? '↑' : '↓'}</span>
                </button>
                {suppressOpen && (
                  <ul className="brief-suppress-list mt-2 space-y-1 text-sm text-[#c2bcd2]" id="brief-suppress-list">
                    {suppressed.reasons.map(r => (
                      <li key={r.reason} className="flex items-baseline gap-2">
                        <span className="font-['DM_Mono'] text-[11px] text-white/50">{r.count}</span>
                        <span>{SUPPRESS_REASON_LABEL[r.reason] ?? r.reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        )}

        {/* ---- Per-source distillation panels (mock §5 comms anatomy, phase 2). The band/task
          * 3-caps route their overflow here; every drill-in item deep-links to the real source.
          * Email + Slack always render at equal height — an empty panel keeps its place with a
          * skeleton (Teddy 2026-07-27: static sections, never drop one). ---- */}
        <SourcePanels
          panels={sourcePanels}
          onOpenCard={setOpenCard}
          onOpenTopic={openTopicObject}
          onNavNote={navNote}
          onAsk={askAbout}
        />

        {/* ---- Role specific sources (mock github-panel.tsx): GitHub in the OverviewCard register
          * beside the GithubStats board on the real stats rail. The board reads LIVE repo state,
          * so on an archived brief the github panel renders alone (temporal-mismatch law). ---- */}
        <section className="mt-9 border-t border-border pt-8" id="band-role-sources">
          <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Program based</p>
              <h2 className="mt-1 font-['Instrument_Serif'] text-[38px] leading-9">Role specific sources</h2>
            </div>
            <p className="max-w-xs text-sm leading-5 text-muted-foreground">
              Signals from the systems where your role has real leverage.
            </p>
          </div>
          <div className={`grid grid-cols-1 items-stretch gap-5 ${readOnly ? '' : 'xl:grid-cols-[minmax(0,1.25fr)_minmax(0,.75fr)]'}`}>
            <GithubSourcePanel
              panels={sourcePanels}
              onOpenCard={setOpenCard}
              onOpenTopic={openTopicObject}
              onNavNote={navNote}
              onAsk={askAbout}
              frozen={readOnly}
            />
            {!readOnly && <GithubStatsBoard onNav={onNav} />}
          </div>
        </section>

        {/* ---- Decisions forming (D7). The decision ledger below records SETTLEMENTS; this is the
          * other half — the questions the team is deciding RIGHT NOW, each with where its members
          * sit. `sharedDecisions` has shipped on every brief since figure b and Slack has rendered
          * it all along ("Where the team leans"); the v2 web renderer had no reader, so a decision
          * reached alone in a chat reached a web reader never. Payload data, so an archived brief
          * renders it too — unlike the live-reading ledger. ---- */}
        {sharedDecisions.length > 0 && (
          <section className="mt-5 rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6" id="band-decisions">
            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
                  Decisions forming
                </p>
                <h2 className="mt-1 font-['Instrument_Serif'] text-[34px] leading-8">Where the team leans</h2>
              </div>
              <Compass size={19} className="text-acc" />
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {sharedDecisions.map((d, di) => (
                <RdDecisionCard
                  key={d.conversationId || `dec-${di}`}
                  decision={d}
                  frozen={readOnly}
                  onOpen={storedBodyFor(d.conversationId) ? () => openDecisionObject(d) : undefined}
                />
              ))}
            </div>
          </section>
        )}

        {/* ---- The record row (mock bottom grid): the Tasks panel beside the Decision ledger.
          * LIVE brief only: the ledger reads the CURRENT record, which under an archived brief
          * would be a temporal mismatch (today's settlements captioned by a past day). The shell
          * itself never collapses — the component renders loading/empty states inside. ---- */}
        {!readOnly && (
          <section className="mt-5 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[minmax(0,.8fr)_minmax(0,1.2fr)]" id="band-record">
            <TasksPanel />
            <DecisionLedger onNav={onNav} />
          </section>
        )}

        {/* ---- People in motion (standup) ---- */}
        {standup.length > 0 && (
          <section className="mt-5 rounded-[24px] border border-border bg-[#f0e9de] p-5 shadow-[0_14px_30px_rgba(88,69,42,.04)] sm:p-6 dark:bg-muted">
            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Mini standup</p>
                <h2 className="mt-1 font-['Instrument_Serif'] text-[34px] leading-8">People in motion</h2>
              </div>
              <div className="flex items-center gap-3">
                {/* The entrance back to the full standup. This band is the MINI standup — the full
                    page (/standup) exists and renders "‹ Briefing", but the redesign port replaced
                    the primitive that carried this link and dropped it, leaving a complete surface
                    that nothing in the app pointed at. */}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onNav('standup')}
                    className="text-sm text-acc underline-offset-2 transition hover:underline"
                  >
                    Full standup →
                  </button>
                )}
                <Users size={19} className="text-acc" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {standup.map((s, i) => {
                const convId = s.conversationIds?.[0] ?? null;
                const inner = (
                  <>
                    <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-[#d9b28e] text-sm font-semibold text-[#4a372b]">
                      {s.initial || s.memberName.charAt(0)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between">
                        <span className="block text-[15px] font-semibold">{s.memberName}</span>
                        {s.memberRole && (
                          <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">{s.memberRole}</span>
                        )}
                      </span>
                      <span className="mt-1 block text-sm leading-5 text-muted-foreground">{s.sentence}</span>
                    </span>
                  </>
                );
                const cls =
                  'flex w-full items-start gap-3 rounded-2xl bg-card p-4 text-left shadow-[0_4px_11px_rgba(88,69,42,.04)] transition';
                return convId ? (
                  <button
                    key={`${s.memberName}-${i}`}
                    data-testid="standup-card-open"
                    className={`${cls} hover:-translate-y-0.5 hover:shadow-md`}
                    onClick={() => openStandupEntry(s)}
                  >
                    {inner}
                  </button>
                ) : (
                  <div key={`${s.memberName}-${i}`} className={cls}>
                    {inner}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ---- The prior-brief receipt (B3 item 2): what happened to the reader's LAST brief.
          * prior-actions.ts has computed these five counts and generate.ts has persisted them on
          * every brief since 2026-07-27, with no reader anywhere. Quiet register, at the foot of
          * the page — it is a receipt, not news. ---- */}
        {priorActions && priorLine && (
          <div className="brief-prior-actions mt-5 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            Your {formatSourceDate(priorActions.date) ?? 'last'} brief: {priorLine}
          </div>
        )}

        {/* ---- Coda: knowledge captured today ---- */}
        {codaItems.length > 0 && (
          <div className="mt-5 rounded-2xl border border-border bg-card px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                className="brief-coda-toggle font-semibold transition hover:text-acc"
                aria-expanded={codaOpen}
                aria-controls="brief-coda-list"
                onClick={() => setCodaOpen(v => {
                  if (!v) track('brief_section_expanded', { section: 'coda', count: codaItems.length });
                  return !v;
                })}
              >
                +{codaItems.length} discover{codaItems.length === 1 ? 'y' : 'ies'} you captured today{' '}
                <span aria-hidden="true">{codaOpen ? '↑' : '↓'}</span>
              </button>
              <span className="text-muted-foreground">—</span>
              <button type="button" className="text-acc transition hover:text-acc-hi" onClick={() => onNav('map')}>
                on your Map →
              </button>
            </div>
            {codaOpen && (
              <ul className="mt-2 space-y-1.5" id="brief-coda-list">
                {codaItems.map((k, i) => (
                  <li key={k.conversationId || `kc-${i}`}>
                    <button
                      type="button"
                      className="brief-coda-item flex w-full items-baseline gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-muted"
                      onClick={() => openCodaItem(k)}
                    >
                      <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 font-['DM_Mono'] text-[9px] uppercase text-muted-foreground">
                        {k.type}
                      </span>
                      <span className="min-w-0 flex-1 text-sm leading-5">{k.text}</span>
                      <span aria-hidden="true" className="text-muted-foreground">→</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ---- Ask launcher — "read the brief → ask about it". ---- */}
        <div className="brief-ask">
          <div className="brief-ask-eyebrow">Ask your team's knowledge</div>
          <AskLauncher onNav={onNav} placeholder="Ask about anything in this briefing…" />
        </div>
      </div>
      {resolveTask && (
        <ConflictResolveModal
          task={resolveTask}
          onResolved={handleResolved}
          onClose={() => setResolveTask(null)}
        />
      )}
      {prepMeeting && (
        <MeetingPrepModal meeting={prepMeeting} onClose={() => setPrepMeeting(null)} onNav={onNav} />
      )}
      {openCard && <RdCardDialog config={openCard} onClose={() => setOpenCard(null)} />}
      {openObjectCard && (
        <RdBriefObjectDialog config={openObjectCard} onClose={() => setOpenObjectCard(null)} />
      )}
    </div>
    </BriefReadTracker>
  );
}
